import { startGame } from '@fg/base/main';
import { Scene } from '@fg/base/scene';
import { AlignGrid } from '@fg/base/phaser_utilities/align_grid';
import store from '@fg/base/store';

let anim;

class AnimatedParticle extends Phaser.GameObjects.Particles.Particle
{
    constructor (emitter)
    {
        super(emitter);

        this.t = 0;
        this.i = Math.floor(Math.random() * 6);
    }

    update (delta, step, processors)
    {
        var result = super.update(delta, step, processors);

        this.t += delta;

        if (this.t >= anim.msPerFrame)
        {
            this.i++;

            if (this.i > 6)
            {
                this.i = 0;
            }

            this.frame = anim.frames[this.i].frame;

            this.t -= anim.msPerFrame;
        }

        return result;
    }
}

class GameScene extends Scene {
    update() {
        super.update();
    }

    init() {
        super.init();
        this.mainSound = this.sound.add('mus_basegame', {"loop":true});
        this.mainSound.play();

        store.commit('toggleExplanation', {
            callback: () => {
                this.letInitialize(() => {
                    this.prepareSounds();
                    this.properties();
                    this.positionerContainer = this.add.container();
                    this.createBg();
                    this.createGameItems();
                    this.loadAnimations();
                    this.spritesReady = true;
                    this.baseGamePositioner();
                    this.canvasReady();
                });
            }
        });
    }

    afterPurchase() {
        this.openedLuckySymbols = [];
        this.openedYourSymbols = [];

        this.groupedOpenedLuckySymbols = {};
        this.groupedOpenedYourSymbols = {};
        this.bonusFound = false;
        this.twinsFound = false;
        this.tileHitRunning = false;
        this.winPlaying = false;
        this.openingAll = false;
        this.twinsMode = false;
        this.shotsFired = false;
        this.symbolsLoading = true;
        this.twinsBeingRevealed = false;
        this.winning = 0;
        this.fontSize = 0;
        this.symbolIndex = 0;
        this.twinsIndex = 0;
        this.stopBigWinIncrement = false;
        this.actionStarted = false;
        this.openedRowsItems = {1: 0, 2: 0, 3: 0};
        this.openingRowsItems = {1: 0, 2: 0, 3: 0};
        this.winPlaying = false;
        this.ticketDone = false;
        this.stopSmoke = false;
        this.bonusSecondStage = false;
        this.openAllClicks = 0;
        this.bonusGamePlayable = false;
        this.bonusEnd = false;
        this.finishingGame = false;
        this.openingRow = false;
        this.afterPurchaseAnimation();

        this.loadSymbols(() => {
            store.commit('showOpenAllButton');
        });
    }

    beforePurchase() {
        this.sound.play('sfx-btn-play');
    }

    prepareSounds() {
        this.pointCountSound = this.sound.add('sfx-point-count', {"loop":true, "volume": 1});
    }

    openItem(item, openAll) {
        if (this.openingRow && this.openingRow !== item.row) {
            return;
        }

        if (this.symbolsLoading 
                || item.twinIsMoving 
                || (item.twinBeingRevealed && item.twinsCover.twinBeingRevealed) 
                || ((this.twinMode && !item.twinInitiated) && !openAll) 
                || this.bonusModeStarted) {
            return;
        }

        this.actionStarted = true;

        this.time.delayedCall(5000, () => {
           this.actionStarted = false;
        });

        this.loadSymbol(item, item.isTwin);

        item.shine.stop();
        item.shine.setAlpha(0);

        if (item.twinInitiated) {
            this.revealTwin(item, false, item.symbol.win);
            return;
        }

        let continueCallback = openAll && ![5, 10, 15].includes(parseInt(item.symbol.place)) ? () => {
            this.openAll(item.row * 5);
        } : false;

        if (item.isTwin) {
            this.startTwin(item, openAll ? () => {
                this.customHelper.removeClickable(item);
                this.customHelper.removeClickable(item.twinsCover);
                this.time.delayedCall(100, () => {
                    this.revealTwin(item, false, item.symbol.win, continueCallback);

                    this.time.delayedCall(200, () => {
                        this.revealTwin(item, true, item.twinsCover.symbol.win, continueCallback);
                    });
                });
            } : false);

            return;
        }

        if (item.symbol.name === 'star') {
            this.startBonus(item, continueCallback);

            return;
        }

        if (!this.openedYourSymbols.includes(item.symbol.place)) {
            this.openedYourSymbols.push(item.symbol.place);
        }

        delete this.unopenedSymbols[item.symbol.place];

        if (item.symbol.name === 'twins') {
            this.showAfter = 0;
            this.showTwins(item, false, continueCallback);

            return;
        }

        if (item.symbol.win) {
            this.openLuckySymbol(item, openAll);
        } else {
            this.openYourSymbol(item, openAll);
        }
    }

    openAll(forceRow) {
        if ((this.openingAll || this.twinMode) && !forceRow) {
            return;
        }

        if (!forceRow) {
            this.openingAll = true;
            this.sound.play('sfx-btn-generic');
        }

        let symbolOpened = false;
        let ends = [5, 10, 15];
        let to = forceRow ? forceRow : 15;

        for (let i = 1; i <= to; i++) {
            const item = this.mainGameItems['your_symbols'][i];

            if (!item.opened) {
                symbolOpened = true;
                this.time.delayedCall(100 + (30 * i), () => {
                    this.openItem(item, true);
                });

                this.openingRow = item.row;
            }

            if (symbolOpened && ends.includes(i)) {
                store.commit('disableOpenAllButton');
                return;
            }
        }
    }

    loadAnimations() {
        let config = {};

        for (let i = 1; i <= 12; i++) {
             config = {
                key: `gold_${i}_flip_down`,
                skipMissedFrames: true,
                frames: this.anims.generateFrameNumbers(`gold_${i}_flip_down`, {
                    start: 0,
                    end: 4
                }),

                frameRate: 25,
            };

            this.anims.create(config);

            config = {
                key: `gold_${i}_flip_up`,
                skipMissedFrames: true,
                frames: this.anims.generateFrameNumbers(`gold_${i}_flip_up`, {
                    start: 0,
                    end: 4
                }),

                frameRate: 25,
            };

            this.anims.create(config);

            config = {
                key: `regular_${i}_flip_down`,
                skipMissedFrames: true,
                frames: this.anims.generateFrameNumbers(`regular_${i}_flip_down`, {
                    start: 0,
                    end: 4
                }),

                frameRate: 25,
            };

            this.anims.create(config);

            config = {
                key: `regular_${i}_flip_up`,
                skipMissedFrames: true,
                frames: this.anims.generateFrameNumbers(`regular_${i}_flip_up`, {
                    start: 0,
                    end: 4
                }),

                frameRate: 25,
            };

            this.anims.create(config);

            config = {
                key: `gold_${i}_win`,
                skipMissedFrames: true,
                frames: this.anims.generateFrameNumbers(`gold_${i}_win`, {
                    start: 0,
                    end: 23
                }),

                frameRate: 25,
            };

            this.anims.create(config);
        }

        config = {
            key: `regular_twins2_flip_up`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`regular_twins2_flip_up`, {
                start: 0,
                end: 4
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: `regular_twins3_flip_up`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`regular_twins3_flip_up`, {
                start: 0,
                end: 4
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: `regular_twins5_flip_up`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`regular_twins5_flip_up`, {
                start: 0,
                end: 4
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: `regular_twins2_flip_down`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`regular_twins2_flip_down`, {
                start: 0,
                end: 4
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: `regular_twins3_flip_down`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`regular_twins3_flip_down`, {
                start: 0,
                end: 4
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: `regular_twins5_flip_down`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`regular_twins5_flip_down`, {
                start: 0,
                end: 4
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: `regular_multiplier2_flip_up`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`regular_multiplier2_flip_up`, {
                start: 0,
                end: 4
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: `regular_multiplier3_flip_up`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`regular_multiplier3_flip_up`, {
                start: 0,
                end: 4
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: `regular_multiplier5_flip_up`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`regular_multiplier5_flip_up`, {
                start: 0,
                end: 4
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: `regular_multiplier2_flip_down`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`regular_multiplier2_flip_down`, {
                start: 0,
                end: 4
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: `regular_multiplier3_flip_down`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`regular_multiplier3_flip_down`, {
                start: 0,
                end: 4
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: `regular_multiplier5_flip_down`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`regular_multiplier5_flip_down`, {
                start: 0,
                end: 4
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: `bonus_flip_down`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`bonus_flip_down`, {
                start: 0,
                end: 4
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: `bonus_flip_up`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`bonus_flip_up`, {
                start: 0,
                end: 4
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: `regular_multiplier2_activate`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`regular_multiplier2_activate`, {
                start: 0,
                end: 22
            }),

            frameRate: 20,
        };

        this.anims.create(config);

        config = {
            key: `regular_multiplier3_activate`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`regular_multiplier3_activate`, {
                start: 0,
                end: 23
            }),

            frameRate: 20,
        };

        this.anims.create(config);

        config = {
            key: `regular_multiplier5_activate`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`regular_multiplier5_activate`, {
                start: 0,
                end: 23
            }),

            frameRate: 20,
        };

        this.anims.create(config);

        config = {
            key: `twins_2_win`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`twins_2_win`, {
                start: 0,
                end: 24
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: `twins_3_win`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`twins_3_win`, {
                start: 0,
                end: 24
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: `twins_5_win`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`twins_5_win`, {
                start: 0,
                end: 24
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: `coin_loop`,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers(`coin_loop`, {
                start: 0,
                end: 6
            }),
            frameRate: 25,
            repeat: -1,
        };

        anim = this.anims.create(config);
    }

    loadSymbols(callback) {
        this.tweens.add({
            targets:  this.mainSound,
            volume:  0.5,
            duration: 500
        });

        this.unopenedSymbols = {};
        this.openedSymbols = {};

        this.time.delayedCall(1000, () => {
            if (Object.keys(this.request.scenario.twins).length > 0) {
                this.twinsMode = true;
                this.showAfter = this.request.scenario.twins['showAfter'];
                this.shots = this.request.scenario.twins['multiplier'];
                this.shotsFired = false;
            }

            if (Object.keys(this.request.scenario.bonus).length > 0) {
                this.bonusMode = true;
                this.bonusModeStarted = false;

                for (let i = 1; i <= 3; i++) {
                    if (this.request.scenario.bonus.symbols[i - 1].win) {
                        this.bonusGameItems[i].isLucky = true;
                    }

                    this.bonusGameItems[i].number.setText(this.request.scenario.bonus.symbols[i - 1].amount);
                    this.bonusGameItems[i].number.amount = this.request.scenario.bonus.symbols[i - 1].amount;
                    this.customHelper.applyDynamicFontSize(this.bonusGameItems[i].number, 280);
                    this.bonusGameItems[i].number.setScale(0);
                    this.bonusGameItems[i].winNumber.setText(this.request.scenario.bonus.symbols.find(el => el.win === true).amount);
                    this.bonusGameItems[i].winNumber.amount = this.request.scenario.bonus.symbols.find(el => el.win === true).amount;
                    this.customHelper.applyDynamicFontSize(this.bonusGameItems[i].winNumber, 280)
                    this.bonusGameItems[i].winNumber.setScale(0);

                }
            }

            for(let i = 1; i <= 5; i++) {
                this.mainGameItems['lucky_symbols'][i].symbol.setTexture(`gold_${this.request.scenario.lucky_symbols[i - 1]}`);
                this.mainGameItems['lucky_symbols'][i].symbol.flip_up.setTexture(`gold_${this.request.scenario.lucky_symbols[i - 1]}_flip_up`);
                this.mainGameItems['lucky_symbols'][i].symbol.flip_down.setTexture(`gold_${this.request.scenario.lucky_symbols[i - 1]}_flip_down`);
                this.mainGameItems['lucky_symbols'][i].symbol.index = this.request.scenario.lucky_symbols[i - 1];
                this.mainGameItems['lucky_symbols'][i].symbol.winAnimation.setTexture(`gold_${this.request.scenario.lucky_symbols[i - 1]}_win`);
            }

            for (let i = 1; i<= 15; i++) {
                let item = this.mainGameItems['your_symbols'][i];
                item.symbol.place = i;

                item.setInteractive({cursor: 'pointer'})
                    .on('pointerup', () => {
                        this.openItem(item);
                        this.sound.play('sfx-click-card-0' + Phaser.Math.RND.integerInRange(1, 3));
                    })
                    .on('pointerover', () => {
                        item.hovered = true;
                        if ((this.twinMode && !item.twinInitiated) || this.bonusModeStarted) {
                            return;
                        }

                        if (this.mainGameItems['your_symbols'][i].isTwin) {
                            this.mainGameItems['your_symbols'][i].setTexture('cover_twins_mouseover');
                        } else {
                            this.mainGameItems['your_symbols'][i].setTexture('cover_mouseover');
                        }
                    })
                    .on('pointerout', () => {
                        item.hovered = false;

                        if ((this.twinMode && !item.twinInitiated) || this.bonusModeStarted) {
                            return;
                        }

                        if (this.mainGameItems['your_symbols'][i].isTwin) {
                            this.mainGameItems['your_symbols'][i].setTexture('cover_twins');
                        } else {
                            this.mainGameItems['your_symbols'][i].setTexture('cover');
                        }
                    });

                this.unopenedSymbols[i] = i;
            }

            this.sound.play('sfx-shuffle-gold');

            for (let i = 1; i <= 5; i++) {
                this.time.delayedCall(i * 40, () => {
                    this.mainGameItems['lucky_symbols'][i].setAlpha(0);
                    this.mainGameItems['lucky_symbols'][i].flip_down.setAlpha(1).play('gold_cover_flip_down');
                    this.mainGameItems['lucky_symbols'][i].flip_down.once('animationcomplete', () => {
                        this.mainGameItems['lucky_symbols'][i].flip_down.setAlpha(0);
                        this.mainGameItems['lucky_symbols'][i].symbol.flip_up.setAlpha(1).play(`gold_${this.request.scenario.lucky_symbols[i - 1]}_flip_up`);
                        this.mainGameItems['lucky_symbols'][i].symbol.flip_up.once('animationcomplete', () => {
                            this.mainGameItems['lucky_symbols'][i].symbol.flip_up.setAlpha(0);
                            this.mainGameItems['lucky_symbols'][i].symbol.setAlpha(1);

                            if (i === 5) {
                                if (callback) {
                                    callback();
                                }
                            }
                        });
                    });
                });
            }
        });

        this.symbolsLoading = false;
        this.enableIdleTimer();
    }

    loadSymbol(item, isTwin = false) {
        if (isTwin) {
            item.symbol.multiplier = false;
            item.twinsCover.symbol.multiplier = false;
            let symbols = this.request.scenario.twins.symbols[this.twinsIndex];
            let multiplierFirst = symbols[0].multiplier !== undefined;
            let multiplierSecond = symbols[1].multiplier !== undefined;
            let spriteFirst;
            let spriteSecond
            let isMultiplier;

            if (multiplierFirst) {
                item.symbol.multiplier = true;
                spriteFirst = 'multiplier' + symbols[0].multiplier;
            } else {
                spriteFirst = symbols[0].symbol;
            }

            if (multiplierSecond) {
                item.twinsCover.symbol.multiplier = true;
                spriteSecond = 'multiplier' + symbols[1].multiplier;
            } else {
                spriteSecond = symbols[1].symbol;
            }

            isMultiplier = multiplierFirst ? 'regular_multiplier' + symbols[0].multiplier : 'regular_' + symbols[0].symbol;

            item.symbol.setTexture(`${multiplierFirst ? 'x' + symbols[0].multiplier : 'regular_' + spriteFirst}`);
            item.symbol.lost.setTexture(`lost_${multiplierFirst ? symbols[0].multiplier + 'x' : symbols[0].symbol}`);
            item.symbol.flip_up.setTexture(`regular_${spriteFirst}_flip_up`);
            item.symbol.flip_down.setTexture(`regular_${spriteFirst}_flip_down`);

            if (!multiplierFirst) {
                item.symbol.winSymbol.setTexture(`gold_${spriteFirst}`);
                item.symbol.winAnimation.setTexture(`gold_${spriteFirst}_win`);
                item.symbol.index = symbols[0].symbol;
                item.symbol.name = symbols[0].symbol;
            } else {
                item.symbol.index = symbols[0].multiplier;
                item.symbol.name = symbols[0].multiplier;
            }

            item.symbol.win = symbols[0].win;
            item.symbol.amount = symbols[0].amount;
            item.symbol.sprite = isMultiplier;

            if (multiplierFirst) {
                item.symbol.multiplierAnimation.setTexture(`regular_multiplier${symbols[0].multiplier}_activate`);
            }

            isMultiplier = multiplierSecond ? 'regular_multiplier' + symbols[1].multiplier : 'regular_' + symbols[1].symbol;

            item.twinsCover.symbol.setTexture(`${multiplierSecond ? 'x' + symbols[1].multiplier : 'regular_' + spriteSecond}`);
            item.twinsCover.symbol.lost.setTexture(`lost_${multiplierSecond ? symbols[1].multiplier + 'x' : symbols[1].symbol}`);
            item.twinsCover.symbol.flip_up.setTexture(`regular_${spriteSecond}_flip_up`);
            item.twinsCover.symbol.flip_down.setTexture(`regular_${spriteSecond}_flip_down`);

            if (!multiplierSecond) {
                item.twinsCover.symbol.winAnimation.setTexture(`gold_${spriteSecond}_win`);
                item.twinsCover.symbol.winSymbol.setTexture(`gold_${spriteSecond}`);
                item.twinsCover.symbol.index = symbols[1].symbol;
                item.twinsCover.symbol.name = symbols[1].symbol;
            } else {
                item.twinsCover.symbol.index = symbols[1].multiplier;
                item.twinsCover.symbol.name = symbols[1].multiplier;
            }

            item.twinsCover.symbol.win = symbols[1].win;
            item.twinsCover.symbol.amount = symbols[1].amount;
            item.twinsCover.symbol.sprite = isMultiplier;

            if (multiplierSecond) {
                item.twinsCover.symbol.multiplierAnimation.setTexture(`regular_multiplier${symbols[1].multiplier}_activate`);
            }
        } else {
            if (this.symbolIndex === this.request.scenario.your_symbols.length) {
                return;
            }

            if (this.request.scenario.your_symbols[this.symbolIndex].symbol === 'twins') {
                item.symbol.setTexture(`twins_${this.request.scenario.your_symbols[this.symbolIndex].multiplier}`);
                item.symbol.flip_up.setTexture(`regular_twins${this.request.scenario.your_symbols[this.symbolIndex].multiplier}_flip_up`);
                item.symbol.flip_down.setTexture(`regular_twins${this.request.scenario.your_symbols[this.symbolIndex].multiplier}_flip_down`);
                item.symbol.winAnimation.setTexture(`twins_${this.request.scenario.your_symbols[this.symbolIndex].multiplier}_win`);
                item.symbol.index = this.request.scenario.your_symbols[this.symbolIndex].multiplier;
                item.symbol.name = this.request.scenario.your_symbols[this.symbolIndex].symbol;
            } else if (this.request.scenario.your_symbols[this.symbolIndex].symbol === 'star') {
                item.symbol.setTexture('bonus_symbol');
                item.symbol.flip_up.setTexture(`bonus_flip_up`);
                item.symbol.flip_down.setTexture(`bonus_flip_down`);
                item.symbol.name = this.request.scenario.your_symbols[this.symbolIndex].symbol;
            } else {
                item.symbol.setTexture(`regular_${this.request.scenario.your_symbols[this.symbolIndex].symbol}`);
                item.symbol.lost.setTexture(`lost_${this.request.scenario.your_symbols[this.symbolIndex].symbol}`);
                item.symbol.flip_up.setTexture(`regular_${this.request.scenario.your_symbols[this.symbolIndex].symbol}_flip_up`);
                item.symbol.flip_down.setTexture(`regular_${this.request.scenario.your_symbols[this.symbolIndex].symbol}_flip_down`);
                item.symbol.winAnimation.setTexture(`gold_${this.request.scenario.your_symbols[this.symbolIndex].symbol}_win`);
                item.symbol.winSymbol.setTexture(`gold_${this.request.scenario.your_symbols[this.symbolIndex].symbol}`);
                item.symbol.index = this.request.scenario.your_symbols[this.symbolIndex].symbol;
                item.symbol.win = this.request.scenario.your_symbols[this.symbolIndex].win;
                item.win_flip_up.setTexture(`gold_${this.request.scenario.your_symbols[this.symbolIndex].symbol}_flip_up`);
                item.win_flip_down.setTexture(`gold_${this.request.scenario.your_symbols[this.symbolIndex].symbol}_flip_down`)
                item.symbol.amount = this.request.scenario.your_symbols[this.symbolIndex].amount;
                item.symbol.twins = this.request.scenario.your_symbols[this.symbolIndex].twins;
                item.symbol.name = this.request.scenario.your_symbols[this.symbolIndex].symbol;
            }
        }
    }

    startTwin(item, callback, delay = false) {
        if (this.twinMode) {
            return;
        }

        store.commit('disableOpenAllButton');
        this.twinMode = true;
        this.globalTwinsOn = true;
        item.twinInitiated = true;
        item.twinIsMoving = true;
        this.openingRowsItems[item.row]++;
        this.openedRowsItems[item.row]++;

        this.mainGameGroup.bringToTop(this.shadow);
        this.tweens.add({
            targets: [this.shadow],
            alpha: 0.6,
            duration: 400,
            ease: 'Power0'
        });

        this.mainGameGroup.bringToTop(item);
        this.mainGameGroup.bringToTop(item.twinsCover);
        this.mainGameGroup.bringToTop(item.twinsGroup);
        this.mainGameGroup.bringToTop(item.cardGroup);

        item.twinsCover.setInteractive({cursor: 'pointer'}).on('pointerup', () => {
            if (this.symbolsLoading || item.twinIsMoving || (this.twinMode && !item.twinInitiated) || this.bonusModeStarted) {
                return;
            }

            this.sound.play('sfx-click-card-0' + Phaser.Math.RND.integerInRange(1, 3));
            
            this.revealTwin(item, true, item.twinsCover.symbol.win);
        }).on('pointerover', () => {
            item.twinsCover.setTexture('cover_twins_mouseover');
        }).on('pointerout', () => {
            item.twinsCover.setTexture('cover_twins');
        });

        this.tweens.add({
            targets: [item],
            scale: 1.2,
            duration: 300,
            ease: 'Power0',
            onComplete: () => {
                item.twinsCover.setScale(1.2).setAlpha(1);
                this.tweens.add({
                    targets: [item.twinsCover],
                    x: item.x + 100,
                    duration: 300,
                    ease: 'Power0',
                    onComplete: () => {
                        item.twinsGroup.iterate((child) => {
                            child.setScale(1.2);
                            child.x = item.twinsCover.x;
                        });
                    }
                });
                this.tweens.add({
                    targets: [item],
                    x: item.x - 100,
                    duration: 300,
                    ease: 'Power0',
                    onComplete: () => {
                        item.cardGroup.iterate((child) => {
                            child.setScale(1.2);
                            child.x = item.x;
                            item.twinIsMoving = false;
                        });

                        if (callback) {
                            callback();
                        }
                    }
                })
            }
        });
    }
    closeTwins(item, isMultiplier = false, callback) {
        if (item.isClosing) {
            return;
        }

        item.isClosing = true;
        this.twinsIndex++;

        if (!item.symbol.win && item.symbol.multiplier) {
            this.tweens.add({
               targets: [item.symbol.lost],
               alpha: 1,
               duration: 200,
               ease: 'Power0',
               onComplete: () => {
                   item.symbol.setAlpha(0);
               }
            });
        }

        if (!item.twinsCover.symbol.win && item.twinsCover.symbol.multiplier) {
            this.tweens.add({
                targets: [item.twinsCover.symbol.lost],
                alpha: 1,
                duration: 200,
                ease: 'Power0',
                onComplete: () => {
                    item.twinsCover.symbol.setAlpha(0);
                }
            });
        }

        if (item.twinsCover.symbol.win) {
            this.mainGameGroup.bringToTop(item.twinsGroup);
        }

        this.time.delayedCall(200, () => {
            this.sound.play('sfx-value-0' + Phaser.Math.RND.integerInRange(1, 5));

            this.tweens.add({
                targets: [item.symbol.lost, item.symbol.winSymbol, item.symbol.number],
                x: item.symbol.number.x + 100,
                duration: 200,
                ease: 'Power0',
                onComplete: () => {
                    item.cardGroup.iterate((child) => {
                        child.x = item.symbol.number.x;
                    });
                }
            });
            this.tweens.add({
                targets: [item.twinsCover.symbol, item.twinsCover.symbol.lost, item.twinsCover.symbol.number, item.twinsCover.symbol.winSymbol],
                x: item.twinsCover.symbol.number.x - 100,
                duration: 200,
                ease: 'Power0',
                onComplete: () => {
                    item.twinsGroup.iterate((child) => {
                        child.x = isMultiplier ? item.twinsCover.symbol.x : item.twinsCover.symbol.lost.x;
                    });
                    this.tweens.add({
                        targets: [this.shadow],
                        alpha: 0,
                        duration: 300,
                        ease: 'Power0'
                    });
                    this.tweens.add({
                        targets: [item.symbol.lost, item.symbol.number, item.twinsCover.symbol, item.twinsCover.symbol.lost, item.symbol.winSymbol, item.twinsCover.symbol.number, item.twinsCover.symbol.winSymbol],
                        scale: 1,
                        duration: 200,
                        ease: 'Power0',
                        onComplete: () => {
                            item.twinsGroup.iterate((child) => {
                                child.setScale(1);
                            });

                            item.cardGroup.iterate((child) => {
                                child.setScale(1);
                            });

                            if (!this.openedYourSymbols.includes(item.symbol.place)) {
                                this.openedYourSymbols.push(item.symbol.place);
                            }

                            delete this.unopenedSymbols[item.symbol.place];
                            this.twinMode = false;
                            item.isTwin = false;
                            item.twinInitiated = false;
                            this.globalTwinsOn = false;
                            this.mainGameGroup.bringToTop(this.frame);
                            this.mainGameGroup.bringToTop(this.pigOne);
                            this.mainGameGroup.bringToTop(this.pigTwo);
                            this.mainGameGroup.bringToTop(this.lucky_symbols);
                            this.rotateTwins(item);
                            this.checkForContinueGame(item);

                            if (callback) {
                                callback();
                            } else {
                                this.enablOpenAll();
                            }
                        }
                    });
                }
            });
        })
    }

    rotateTwins(item) {
        if (item.symbol.win) {
            item.placeSymbol = true;
        } else {
            item.placeSymbol = false;
        }

        this.tweens.add({
            targets: [item.twinsCover.twinsFrame, item.twinsFrame],
            alpha: 1,
            duration: 300,
            ease: 'Power0',
        });

        item.rotationToTwins = this.time.addEvent({
            delay: 2000,
            callback: () => {
                this.rotateToTwins(item);
            },
            loop: true
        });
    }

    rotateToTwins(item, instaRotation) {
        let duration = instaRotation ? 0 : 300;

        if (item.placeSymbol) {
            item.symbol.setAlpha(1);

            this.tweens.add({
                targets: [item.symbol.number],
                scale: 0,
                duration: duration,
                ease: 'Power0'
            });


            if(!item.twinsCover.symbol.multiplier && !item.symbol.win) {
                item.symbol.setAlpha(0);
            }

            this.tweens.add({
                targets: [item.symbol.lost, item.symbol, item.symbol.winSymbol, item.twinsCover.symbol],
                alpha: 0,
                duration: duration,
                ease: 'Power0',
                onComplete: () => {
                    if (item.symbol.win) {
                        if (!item.twinsCover.symbol.multiplier) {
                            this.tweens.add({
                                targets: [item.twinsCover.symbol.number],
                                scale: 1,
                                alpha: 1,
                                ease: duration,
                                duration: duration,
                            });

                            if (item.twinsCover.symbol.win) {
                                this.tweens.add({
                                    targets: [item.twinsCover.symbol.winSymbol],
                                    alpha: 1,
                                    ease: duration,
                                    duration: duration,
                                });
                            } else {
                                this.tweens.add({
                                    targets: [item.twinsCover.symbol.lost],
                                    alpha: 1,
                                    ease: duration,
                                    duration: duration,
                                });
                            }
                        } else {
                            this.tweens.add({
                                targets: [item.symbol.win ? item.twinsCover.symbol : item.twinsCover.symbol.lost],
                                alpha: 1,
                                ease: duration,
                                duration: duration,
                            });
                        }
                    } else {
                        if (!item.twinsCover.symbol.multiplier) {
                            this.tweens.add({
                                targets: [item.twinsCover.symbol.number],
                                scale: 1,
                                alpha: 1,
                                ease: duration,
                                duration: duration,
                            });

                            if (item.twinsCover.symbol.win) {
                                this.tweens.add({
                                    targets: [item.twinsCover.symbol.winSymbol],
                                    alpha: 1,
                                    ease: duration,
                                    duration: duration,
                                });
                            } else {
                                this.tweens.add({
                                    targets: [item.twinsCover.symbol.lost],
                                    alpha: 1,
                                    ease: duration,
                                    duration: duration,
                                });
                            }
                        } else {
                            this.tweens.add({
                                targets: [item.symbol.win ? item.twinsCover.symbol : item.twinsCover.symbol.lost],
                                alpha: 1,
                                ease: duration,
                                duration: duration,
                            });
                        }
                    }

                    item.placeSymbol = false;
                }
            });
        } else {
            this.tweens.add({
                targets: [item.twinsCover.symbol.number],
                scale: 0,
                duration: duration,
                ease: 'Power0'
            });

            if(!item.symbol.multiplier && !item.twinsCover.symbol.win) {
                item.twinsCover.symbol.setAlpha(0);
            }

            this.tweens.add({
                targets: [item.twinsCover.symbol.lost, item.twinsCover.symbol, item.twinsCover.symbol.winSymbol],
                alpha: 0,
                duration: duration,
                ease: 'Power0',
                onComplete: () => {
                        if (!item.symbol.multiplier) {
                            this.tweens.add({
                                targets: [item.symbol.number],
                                scale: 1,
                                alpha: 1,
                                ease: duration,
                                duration: duration,
                            });

                            if (item.symbol.win) {
                                this.tweens.add({
                                    targets: [item.symbol.winSymbol],
                                    alpha: 1,
                                    ease: 'Power0',
                                    duration: duration,
                                });
                            } else {
                                this.tweens.add({
                                    targets: [item.symbol.lost],
                                    alpha: 1,
                                    ease: duration,
                                    duration: duration,
                                });
                            }
                    } else {
                        this.tweens.add({
                            targets: [item.twinsCover.symbol.win ? item.symbol : item.symbol.lost],
                            alpha: 1,
                            ease: duration,
                            duration: duration,
                        });
                    }

                    item.placeSymbol = true;
                }
            });
        }
    }

    revealTwin(item, right = false, win = false, callback) {
        let isMultiplier = true;

        if (right) {
            this.customHelper.removeClickable(item.twinsCover);
            item.twinsCover.symbol.number.setScale(0).setAlpha(0);
            isMultiplier = item.twinsCover.symbol.multiplier;
        } else {
            this.customHelper.removeClickable(item);
            item.symbol.number.setScale(0);
            isMultiplier = item.symbol.multiplier;
        }

        this.sound.play('sfx-value-0' + Phaser.Math.RND.integerInRange(1, 5));
        
        if (right && !win) {
            item.opened = true;

            if (!isMultiplier) {
                item.twinsCover.symbol.number.setText(item.twinsCover.symbol.amount).setAlpha(1).setFill(this.createGradientSilver(item.twinsCover.symbol.number));
            }

            item.twinsCover.symbol.number.setScale(0);
            item.twinsCover.setAlpha(0);
            item.twinsCover.flip_down.setAlpha(1);
            item.twinsCover.flip_down.play(`regular_cover_twins_flip_down`);
            item.twinsCover.flip_down.once('animationcomplete', () => {
                item.twinsCover.flip_down.setAlpha(0);
                item.twinsCover.symbol.flip_up.setAlpha(1);
                item.twinsCover.symbol.flip_up.play(`${item.twinsCover.symbol.sprite}_flip_up`);
                item.twinsCover.symbol.flip_up.once('animationcomplete', () => {
                    item.twinsCover.symbol.flip_up.setAlpha(0);
                    item.twinsCover.symbol.setAlpha(1);
                    item.twinsCover.symbol.number.setScale(1);
                    this.customHelper.applyDynamicFontSize(item.twinsCover.symbol.number, 155);
                    item.twinsCover.symbol.number.setScale(0);
                    this.playRandomValuePoint();
                    this.tweens.add({
                        targets: [item.twinsCover.symbol.number],
                        scale: 1.2,
                        duration: 200,
                        ease: 'Power0',
                        onComplete: () => {
                            item.twinsCount++;
                            item.twinsCover.symbol.setAlpha(1);

                            if (!isMultiplier) {
                                this.tweens.add({
                                    targets: [item.twinsCover.symbol.lost],
                                    alpha: 1,
                                    duration: 200,
                                    ease: 'Power0',
                                    onComplete: () => {
                                        item.twinsCover.symbol.setAlpha(0);
                                        if (item.twinsCount === 2) {
                                            this.closeTwins(item, isMultiplier, callback);
                                        }
                                    }
                                });
                            } else {
                                if (item.twinsCount === 2) {
                                    if (item.symbol.win) {
                                        this.multiplyTwins(item, callback);
                                    } else {
                                        this.closeTwins(item, isMultiplier, callback);
                                    }
                                }
                            }
                        }
                    });
                });
            });
        } else if (!right && !win) {
            item.opened = true;
            item.symbol.number.setScale(0);
            item.setAlpha(0);
            item.twins_flip_down.setAlpha(1);
            item.twins_flip_down.play(`regular_cover_twins_flip_down`);
            item.twins_flip_down.once('animationcomplete', () => {
                item.twins_flip_down.setAlpha(0);
                item.symbol.flip_up.setAlpha(1);
                item.symbol.flip_up.play(`${item.symbol.sprite}_flip_up`);
                item.symbol.flip_up.once('animationcomplete', () => {
                    item.symbol.setAlpha(1);
                    item.symbol.flip_up.setAlpha(0);


                    item.symbol.number.setText(item.symbol.amount).setAlpha(1).setFill(this.createGradientSilver(item.symbol.number));
                    item.symbol.number.setScale(1);
                    this.customHelper.applyDynamicFontSize(item.symbol.number, 155);
                    item.symbol.number.setScale(0);

                    if (isMultiplier) {
                        item.symbol.number.setAlpha(0)
                    }

                    this.playRandomValuePoint();

                    this.tweens.add({
                        targets: [item.symbol.number],
                        scale: 1.2,
                        duration: 200,
                        ease: 'Power0',
                        onComplete: () => {
                            item.symbol.setAlpha(1);
                            item.twinsCount++;

                            if (!isMultiplier) {
                                this.tweens.add({
                                    targets: [item.symbol.lost],
                                    alpha: 1,
                                    duration: 200,
                                    ease: 'Power0',
                                    onComplete: () => {
                                        item.symbol.setAlpha(0);

                                        if (item.twinsCount ===  2) {
                                            this.closeTwins(item, isMultiplier, callback);
                                        }
                                    }
                                });
                            } else {
                                if (item.twinsCount === 2) {
                                    if (item.twinsCover.symbol.win) {
                                        this.multiplyTwins(item, callback);
                                    } else {
                                        this.closeTwins(item, isMultiplier, callback);
                                    }
                                }
                            }
                        }
                    });
                });
            });
        } else if (right && win) {
            this.showTwinsWinOnRight(item, isMultiplier, callback);
        } else if (!right && win) {
            this.showTwinsWinOnLeft(item, isMultiplier, callback);
        }
    }

    swapCardPlaces(item) {
        let cardX = item.x;
        let twinsX = item.twinsCover.x;

        item.twinsCover.x = cardX;
        item.x = twinsX;

        item.cardGroup.iterate((children) => {
           children.x = twinsX;
        });

        item.twinsGroup.iterate((children) => {
           children.x = cardX;
        });

        return item;
    }

    multiplyTwins(item, callback) {
        if (item.symbol.multiplier) {
            item.symbol.setAlpha(0);
            item.symbol.multiplierAnimation.setAlpha(1);
            item.symbol.multiplierAnimation.play(`regular_multiplier${item.symbol.index}_activate`);
            this.twinsIndex++;

            this.mainGameGroup.bringToTop(item.twinsGroup);

            this.tweens.add({
                targets: [item.twinsCover.symbol.winSymbol, item.twinsCover.symbol.number],
                x: item.twinsCover.symbol.x + 75,
                duration: 400,
                ease: 'Bounce',
            });
            this.tweens.add({
                targets: [item.symbol.multiplierAnimation],
                x: item.symbol.x - 75,
                duration: 400,
                ease: 'Bounce',
                onComplete: () => {
                    this.sound.play('sfx-value-0' + Phaser.Math.RND.integerInRange(1, 5));
                    this.tweens.add({
                        targets: [item.twinsCover.symbol.winSymbol, item.twinsCover.symbol.number],
                        x: item.symbol.number.x + 100,
                        duration: 300,
                        ease: 'Sine',
                    });
                    this.tweens.add({
                        targets: [item.symbol.multiplierAnimation],
                        x: item.symbol.number.x + 100,
                        duration: 300,
                        ease: 'Sine',
                        onComplete: () => {
                            this.winning += (item.twinsCover.symbol.amount - item.symbol.amount);
                            store.commit('setAmountWon', this.winning);

                            item.cardGroup.iterate((child) => {
                                child.x = item.twinsCover.symbol.winSymbol.x;
                            })

                            item.twinsGroup.iterate((child) => {
                                child.x = item.symbol.multiplierAnimation.x;
                            })

                            item.twinsCover.symbol.setAlpha(0);
                            item.twinsCover.symbol.impact.setAlpha(1).play('impact');

                            if (item.twinsCover.moneyParticle) {
                                item.twinsCover.moneyParticle.destroy();
                            }

                            item.twinsCover.moneyParticle = this.add.particles('coin_loop');
                            item.twinsCover.moneyEmitter = item.twinsCover.moneyParticle.createEmitter({
                                x: item.cardGroup.x,
                                y: item.cardGroup.y,
                                lifespan: 2000,
                                speedY: { min: 100, max: -500 },
                                speedX: { min: -300, max: 300 },
                                particleClass: AnimatedParticle,
                                gravityY: 1000,
                                quantity: 1,
                                on: false
                            });
            
                            item.twinsCover.moneyEmitter.explode(30);

                            this.sound.play('sfx-magic-coins-0' + Phaser.Math.RND.integerInRange(1, 3));

                            item.twinsCover.symbol.winAnimation.setAlpha(1).play(`gold_${item.twinsCover.symbol.index}_win`).once('animationcomplete', () => {
                                item.twinsCover.symbol.impact.setAlpha(0);
                                item.twinsCover.symbol.winAnimation.setAlpha(0);
                                item.twinsCover.symbol.winSymbol.setAlpha(1);
                                item.symbol.multiplierAnimation.setAlpha(0);
                                this.tweens.add({
                                    targets: [this.shadow],
                                    alpha: 0,
                                    duration: 400,
                                    ease: 'Power0'
                                });
                                this.tweens.add({
                                    targets: [item.twinsCover.symbol.winSymbol, item.twinsCover.symbol.number, item.symbol, item.symbol.number],
                                    scale: 1,
                                    duration: 300,
                                    ease: 'Power0',
                                    onComplete: () => {
                                        item.twinsGroup.iterate((child) => {
                                            child.setScale(1);
                                        });

                                        item.cardGroup.iterate((child) => {
                                            child.setScale(1);
                                        });

                                        if (!this.openedYourSymbols.includes(item.symbol.place)) {
                                            this.openedYourSymbols.push(item.symbol.place);
                                        }

                                        delete this.unopenedSymbols[item.symbol.place];

                                        this.twinMode = false;
                                        item.isTwin = false;
                                        this.globalTwinsOn = false;
                                        item.twinInitiated = false;
                                        this.mainGameGroup.bringToTop(this.frame);
                                        this.mainGameGroup.bringToTop(this.pigOne);
                                        this.mainGameGroup.bringToTop(this.pigTwo);
                                        this.mainGameGroup.bringToTop(this.lucky_symbols);
                                        this.rotateTwins(item);
                                        this.checkForContinueGame(item);

                                        if (callback) {
                                            callback();
                                        } else {
                                            this.enablOpenAll();
                                        }
                                    }
                                });
                            });
                            item.twinsCover.symbol.number.setText(item.symbol.amount * item.symbol.index).setFill(this.createGradientGold(item.symbol.number));
                            this.customHelper.applyDynamicFontSize(item.twinsCover.symbol.number, 155);
                        }
                    });
                }
            });
        } else {
            item.twinsCover.symbol.setAlpha(0);
            item.twinsCover.symbol.multiplierAnimation.setAlpha(1);
            item.twinsCover.symbol.multiplierAnimation.play(`regular_multiplier${item.twinsCover.symbol.index}_activate`);
            this.twinsIndex++;

            this.tweens.add({
                targets: [item.symbol.winSymbol, item.symbol.number],
                x: item.symbol.x - 75,
                duration: 400,
                ease: 'Bounce',
            });
            this.tweens.add({
                targets: [item.twinsCover.symbol.multiplierAnimation],
                x: item.twinsCover.symbol.x + 75,
                duration: 400,
                ease: 'Bounce',
                onComplete: () => {
                    this.sound.play('sfx-value-0' + Phaser.Math.RND.integerInRange(1, 5));
                    this.tweens.add({
                        targets: [item.symbol.winSymbol, item.symbol.number],
                        x: item.twinsCover.symbol.number.x - 100,
                        duration: 300,
                        ease: 'Sine',
                    });
                    this.tweens.add({
                        targets: [item.twinsCover.symbol.multiplierAnimation],
                        x: item.twinsCover.symbol.number.x - 100,
                        duration: 300,
                        ease: 'Sine',
                        onComplete: () => {
                            this.winning += (item.symbol.amount - item.twinsCover.symbol.amount);

                            store.commit('setAmountWon', this.winning);

                            item.cardGroup.iterate((child) => {
                                child.x = item.symbol.winSymbol.x;
                            })

                            item.twinsGroup.iterate((child) => {
                                child.x = item.twinsCover.symbol.multiplierAnimation.x;
                            })

                            item.symbol.setAlpha(0);
                            item.symbol.impact.setAlpha(1).play('impact');

                            if (item.moneyParticle) {
                                item.moneyParticle.destroy();
                            }
            
                            item.moneyParticle = this.add.particles('coin_loop');
                            item.moneyEmitter = item.moneyParticle.createEmitter({
                                x: item.cardGroup.x,
                                y: item.cardGroup.y,
                                lifespan: 2000,
                                speedY: { min: 100, max: -500 },
                                speedX: { min: -300, max: 300 },
                                particleClass: AnimatedParticle,
                                gravityY: 1000,
                                quantity: 1,
                                on: false
                            });

                            item.moneyEmitter.explode(30);

                            this.sound.play('sfx-magic-coins-0' + Phaser.Math.RND.integerInRange(1, 3));

                            item.symbol.winAnimation.setAlpha(1).play(`gold_${item.symbol.index}_win`).once('animationcomplete', () => {
                                item.symbol.impact.setAlpha(0);
                                item.symbol.winAnimation.setAlpha(0);
                                item.symbol.winSymbol.setAlpha(1);
                                item.twinsCover.symbol.multiplierAnimation.setAlpha(0);
                                this.tweens.add({
                                    targets: [this.shadow],
                                    alpha: 0,
                                    duration: 400,
                                    ease: 'Power0'
                                });
                                this.tweens.add({
                                    targets: [item.symbol.winSymbol, item.symbol.number, item.twinsCover.symbol, item.twinsCover.symbol.number],
                                    scale: 1,
                                    duration: 300,
                                    ease: 'Power0',
                                    onComplete: () => {
                                        item.twinsGroup.iterate((child) => {
                                            child.setScale(1);
                                        });

                                        item.cardGroup.iterate((child) => {
                                            child.setScale(1);
                                        });

                                        if (!this.openedYourSymbols.includes(item.symbol.place)) {
                                            this.openedYourSymbols.push(item.symbol.place);
                                        }

                                        delete this.unopenedSymbols[item.symbol.place];

                                        this.twinMode = false;
                                        item.isTwin = false;
                                        this.globalTwinsOn = false;
                                        item.twinInitiated = false;
                                        this.mainGameGroup.bringToTop(this.frame);
                                        this.mainGameGroup.bringToTop(this.pigOne);
                                        this.mainGameGroup.bringToTop(this.pigTwo);
                                        this.mainGameGroup.bringToTop(this.lucky_symbols);
                                        this.rotateTwins(item);
                                        this.checkForContinueGame(item);

                                        if (callback) {
                                            callback();
                                        } else {
                                            this.enablOpenAll();
                                        }
                                    }
                                });
                            });
                            item.symbol.number.setText(item.twinsCover.symbol.amount * item.twinsCover.symbol.index).setFill(this.createGradientGold(item.symbol.number));
                            this.customHelper.applyDynamicFontSize(item.symbol.number, 155);
                        }
                    });
                }
            });
        }
    }

    showTwinsWinOnRight(item, isMultiplier, callback) {
        item.opened = true;
        item.twinsCover.symbol.number.setScale(0);
        item.twinsCover.symbol.setAlpha(0);
        item.twinsCover.setAlpha(0);
        item.twinsCover.flip_down.setAlpha(1);
        item.twinsCover.flip_down.play(`regular_cover_twins_flip_down`);

        this.playRandomValuePoint();

        item.twinsCover.flip_down.once('animationcomplete', () => {
            item.twinsCover.flip_down.setAlpha(0);
            item.twinsCover.symbol.flip_up.setAlpha(1);
            item.twinsCover.symbol.flip_up.play(`${item.twinsCover.symbol.sprite}_flip_up`);
            item.twinsCover.symbol.flip_up.once('animationcomplete', () => {
                item.twinsCover.symbol.setAlpha(1);
                item.twinsCover.symbol.flip_up.setAlpha(0);

                if (item.symbol.multiplier) {
                    let amount = item.symbol.multiplier ? item.symbol.amount : item.twinsCover.symbol.amount
                    item.twinsCover.symbol.number.setText(amount).setStyle(this.fonts.winYellow).setAlpha(1).setFill(this.createGradientGold(item.twinsCover.symbol.number));
                } else {
                    if (item.twinsCover.symbol.win) {
                        item.twinsCover.symbol.number.setText(item.twinsCover.symbol.amount).setStyle(this.fonts.winYellow).setAlpha(1).setFill(this.createGradientGold(item.twinsCover.symbol.number));
                    } else {
                        item.twinsCover.symbol.number.setText(item.twinsCover.symbol.amount).setStyle(this.fonts.normalSilver).setAlpha(1).setFill(this.createGradientSilver(item.twinsCover.symbol.number));
                    }
                }

                item.twinsCover.symbol.number.setScale(0);
                this.customHelper.applyDynamicFontSize(item.symbol.number, 155);
                item.twinsCover.symbol.number.setScale(1);

                this.playRandomValuePoint();

                this.tweens.add({
                    targets: [item.twinsCover.symbol.number],
                    scale: 1.2,
                    duration: 200,
                    ease: 'Power0',
                    onComplete: () => {
                        let luckySymbol = Object.values(this.mainGameItems['lucky_symbols']).find(el => el.symbol.index === item.twinsCover.symbol.index);
                        luckySymbol.symbol.winAnimation.setAlpha(1);
                        luckySymbol.symbol.winAnimation.play(`gold_${luckySymbol.symbol.index}_win`);

                        if (!item.twinsCover.symbol.multiplier) {
                            if (item.symbol.multiplier) {
                                this.winning += item.symbol.amount;
                            } else {
                                this.winning += item.twinsCover.symbol.amount;
                            }
                            
                            store.commit('setAmountWon', this.winning);
                        }

                        if (item.twinsCover.moneyParticle) {
                            item.twinsCover.moneyParticle.destroy();
                        }

                        item.twinsCover.moneyParticle = this.add.particles('coin_loop');
                        item.twinsCover.moneyEmitter = item.twinsCover.moneyParticle.createEmitter({
                            x: { min: (item.twinsGroup.x + 100) - 10, max: (item.twinsGroup.x + 100) + 10 },
                            y: item.twinsGroup.y,
                            lifespan: 2000,
                            speedY: { min: 100, max: -500 },
                            speedX: { min: -300, max: 300 },
                            particleClass: AnimatedParticle,
                            gravityY: 1000,
                            quantity: 1,
                            on: false
                        });
        
                        item.twinsCover.moneyEmitter.explode(30);

                        this.sound.play('sfx-magic-coins-0' + Phaser.Math.RND.integerInRange(1, 3));

                        item.twinsCover.symbol.winAnimation.setAlpha(1);
                        item.twinsCover.symbol.winAnimation.play(`gold_${item.twinsCover.symbol.index}_win`).once('animationcomplete', () => {
                            item.twinsCover.symbol.setAlpha(0);
                            item.twinsCover.symbol.winAnimation.setAlpha(0);
                            luckySymbol.symbol.winAnimation.setAlpha(0);
                            item.twinsCover.symbol.winSymbol.setAlpha(1);

                            item.twinsCount++;

                            if (item.twinsCount === 2) {
                                if (!item.symbol.multiplier && !item.twinsCover.symbol.multiplier) {
                                    this.closeTwins(item, isMultiplier, callback);
                                } else {
                                    this.multiplyTwins(item, callback);
                                }
                            }
                        });
                        }
                });
            });
        });
    }

    showTwinsWinOnLeft(item, isMultiplier, callback) {
        item.opened = true;
        item.symbol.number.setScale(0);
        item.setAlpha(0);
        item.twins_flip_down.setAlpha(1);
        item.twins_flip_down.play(`regular_cover_twins_flip_down`);
        this.playRandomValuePoint();
        item.twins_flip_down.once('animationcomplete', () => {
            item.twins_flip_down.setAlpha(0);
            item.symbol.flip_up.setAlpha(1);
            item.symbol.flip_up.play(`${item.symbol.sprite}_flip_up`);
            item.symbol.flip_up.once('animationcomplete', () => {
                item.symbol.setAlpha(1);
                item.symbol.flip_up.setAlpha(0);

                if (item.twinsCover.symbol.multiplier) {
                    let amount = item.twinsCover.symbol.multiplier ? item.twinsCover.symbol.amount : item.symbol.amount
                    item.symbol.number.setText(amount).setStyle(this.fonts.winYellow).setAlpha(1).setFill(this.createGradientGold(item.symbol.number));
                } else {
                    if (item.symbol.win) {
                        item.symbol.number.setText(item.symbol.amount).setStyle(this.fonts.winYellow).setAlpha(1).setFill(this.createGradientGold(item.symbol.number));
                    } else {
                        item.symbol.number.setText(item.symbol.amount).setStyle(this.fonts.normalSilver).setAlpha(1).setFill(this.createGradientSilver(item.symbol.number));
                    }
                }

                item.symbol.number.setScale(0);
                this.customHelper.applyDynamicFontSize(item.symbol.number, 155);
                item.symbol.number.setScale(1);

                this.playRandomValuePoint();

                this.tweens.add({
                    targets: [item.symbol.number],
                    scale: 1.2,
                    duration: 200,
                    ease: 'Power0',
                    onComplete: () => {
                        // item.symbol.setAlpha(0);
                        // item.symbol.winAnimation.setAlpha(1);
                        // item.symbol.winAnimation.play(`gold_${item.symbol.index}_win`)
                        //
                        // let luckySymbol = Object.values(this.mainGameItems['lucky_symbols']).find(el => el.symbol.index === item.symbol.index);
                        // luckySymbol.symbol.winAnimation.setAlpha(1);
                        // luckySymbol.symbol.winAnimation.play(`gold_${luckySymbol.symbol.index}_win`);
                        // item.symbol.winAnimation.play(`gold_${item.symbol.index}_win`)
                        // item.moneyParticle = this.add.particles('coin_loop');
                        // item.moneyEmitter = item.moneyParticle.createEmitter({
                        //     x: { min: item.cardGroup.x - 110, max: item.cardGroup.x - 100 },
                        //     y: item.cardGroup.y,
                        //     frame: 0,
                        //     quantity: 1,
                        //     gravity: 5,
                        //     frequency: 0,
                        //     speed: 200,
                        //     gravityY: 300,
                        //     angle: {min:-150, max: 300},
                        //     particleClass: AnimatedParticle,
                        //     maxParticles: 30
                        // });
                        // item.symbol.winAnimation.once('animationcomplete', () => {
                        // if (!item.symbol.win) {
                        //     this.tweens.add({
                        //         targets: [item.symbol.lost],
                        //         alpha: 1,
                        //         duration: 200,
                        //         easge: 'Power0',
                        //         onComplete: () => {
                        //             item.symbol.setAlpha(0);
                        //             if (item.twinsCount === 2) {
                        //                 this.closeTwins(item, isMultiplier, callback);
                        //             }
                        //         }
                        //     });
                        let luckySymbol = Object.values(this.mainGameItems['lucky_symbols']).find(el => el.symbol.index === item.symbol.index);
                        luckySymbol.symbol.winAnimation.setAlpha(1);
                        luckySymbol.symbol.winAnimation.play(`gold_${luckySymbol.symbol.index}_win`);

                        if (item.moneyParticle) {
                            item.moneyParticle.destroy();
                        }

                        item.moneyParticle = this.add.particles('coin_loop');
                        item.moneyEmitter = item.moneyParticle.createEmitter({
                            x: { min: (item.cardGroup.x - 100) - 10 ,max: (item.cardGroup.x - 100) + 10 },
                            y: item.cardGroup.y,
                            lifespan: 2000,
                            speedY: { min: 100, max: -500 },
                            speedX: { min: -300, max: 300 },
                            particleClass: AnimatedParticle,
                            gravityY: 1000,
                            quantity: 1,
                            on: false
                        });

                        item.moneyEmitter.explode(30);
                        
                        this.sound.play('sfx-magic-coins-0' + Phaser.Math.RND.integerInRange(1, 3));

                        item.symbol.winAnimation.setAlpha(1);
                        item.symbol.winAnimation.play(`gold_${item.symbol.index}_win`).once('animationcomplete', () => {
                            item.symbol.setAlpha(0);
                            item.symbol.winAnimation.setAlpha(0);
                            luckySymbol.symbol.winAnimation.setAlpha(0);
                            item.symbol.winSymbol.setAlpha(1);

                            item.twinsCount++;

                            if (!item.symbol.multiplier) {
                                if (item.twinsCover.symbol.multiplier) {
                                    this.winning += item.twinsCover.symbol.amount;
                                } else {
                                    this.winning += item.symbol.amount;
                                }
                                
                                store.commit('setAmountWon', this.winning);
                            }

                            if (item.twinsCount === 2) {
                                if (!item.symbol.multiplier && !item.twinsCover.symbol.multiplier) {
                                    this.closeTwins(item, isMultiplier, callback);
                                } else {
                                    this.multiplyTwins(item, callback);
                                }
                            }
                        });
                    }
                });
                item.symbol.setAlpha(1);
            });
        });
    }

    startBonus(item, callback) {
        if (this.twinMode || this.bonusModeStarted) {
            this.openAllBonusMode = false;
            return;
        }

        this.customHelper.removeClickable(item);
        item.symbol.setTexture('bonus_symbol');
        item.symbol.lost.setAlpha(0);
        item.opened = true;
        store.commit('disableOpenAllButton');
        this.symbolIndex++;
        this.bonusModeStarted = true;
        item.setAlpha(0);
        item.flip_down.setAlpha(1);
        item.flip_down.play(`regular_cover_flip_down`);
        item.flip_down.once('animationcomplete', () => {
            item.flip_down.setAlpha(0);
            item.symbol.flip_up.setAlpha(1);
            item.symbol.flip_up.play(`bonus_flip_up`);
            item.symbol.flip_up.once('animationcomplete', () => {
                item.symbol.flip_up.setAlpha(0);
                item.symbol.impact.setAlpha(1).play('impact');
                item.symbol.number.setText(this.request.scenario.bonus.symbols.find(el => el.win === true).amount).setStyle(this.fonts.winYellow);
                item.symbol.number.setScale(1);
                item.symbol.number.setAlpha(1);
                this.customHelper.applyDynamicFontSize(item.symbol.number, 155);
                item.symbol.number.setScale(0);
                item.symbol.setAlpha(1)
                this.startBonusGame(item, callback);

                item.symbol.impact.once('animationcomplete', () => {
                    item.symbol.impact.setAlpha(0);
                });
            });
        });
    }

    startBonusGame(item, callback) {
        this.mainGameGroup.bringToTop(this.shadow);
        this.mainGameGroup.bringToTop(item);
        this.mainGameGroup.bringToTop(item.group);
        this.tweens.add({
            targets: [this.shadow],
            alpha: 0.6,
            duration: 300,
            ease: 'Power0',
            onComplete: () => {
                this.mainGameGroup.bringToTop(this.bonusGroup);

                this.sound.play('sfx-bonus-cards-up');

                for (let i = 1; i <= 3; i++) {
                    let index = 0;
                    this.time.delayedCall(100 + (i * 100), () => {
                        this.bonusGameItems[i].setAlpha(1);
                        this.bonusGameItems[i].tween = this.tweens.add({
                           targets: [this.bonusGameItems[i]],
                           y: (this.alignGrid.getIndexPos(195 + index).y * -1) - 50,
                           duration: 500,
                           ease: 'Power1',
                           onComplete: () => {
                               this.bonusGameItems[i].group.iterate((children) => {
                                  children.y = this.bonusGameItems[i].y;
                               });
                           }
                        });
                    });
                    index += 5;
                }

                this.time.delayedCall(1500, () => {
                    this.sound.play('sfx-bonus-cards-turn');
                    for (let i = 1; i <= 3; i++) {
                        this.time.delayedCall(100 + (i * 100), () => {
                            this.bonusGameItems[i].setAlpha(0);
                            this.bonusGameItems[i].number.setFill(this.createGradientGold(this.bonusGameItems[i].number));
                            this.bonusGameItems[i].winNumber.setFill(this.createGradientGold(this.bonusGameItems[i].winNumber));
                            this.bonusGameItems[i].flip_up.setAlpha(1).play('bonus_card_flip_up');
                            this.bonusGameItems[i].number.setAlpha(1);

                            this.time.delayedCall(150 + (i * 100), () => {
                                this.sound.play('sfx-bonus-cards-value-0' + i);
                                this.tweens.add({
                                    targets: [this.bonusGameItems[i].number],
                                    scale: 1,
                                    duration: 300,
                                    ease: 'Power0',
                                    completeDelay: 1000,
                                    onComplete: () => {
                                        this.time.delayedCall(400 + (i * 100), () => {
                                            this.sound.play('sfx-bonus-cards-value-disappear');
                                            this.tweens.add({
                                                targets: [this.bonusGameItems[i].number],
                                                scale: 0,
                                                duration: 300,
                                                ease: 'Power0'
                                            });
                                        });
                                    }
                                })
                            })
                        });
                    }

                    this.time.delayedCall(2800, () => {
                        this.bonusSecondStage = true;

                        for (let i = 1; i <= 3; i++) {
                            this.time.delayedCall((i * 50), () => {
                                this.bonusGameItems[i].flip_up.setAlpha(0);
                                this.bonusGameItems[i].flip_down.setAlpha(1).play('bonus_card_flip_down');
                            })
                        }
                            this.time.delayedCall(800, () => {
                                this.bonusGameItems[1].setInteractive({cursor: 'pointer'})
                                    .on('pointerup', () => {
                                        if (this.bonusCardRevealed || !this.bonusGamePlayable) {
                                            return;
                                        }

                                        this.revealBonusCard(this.bonusGameItems[1], callback, 1, item);
                                    })
                                    .on('pointerover', () => {
                                        if (this.bonusCardRevealed || !this.bonusGamePlayable) {
                                            return;
                                        }

                                        this.bonusGameItems[1].setScale(0.95);
                                    })
                                    .on('pointerout', () => {
                                        if (this.bonusCardRevealed || !this.bonusGamePlayable) {
                                            return;
                                        }

                                        this.bonusGameItems[1].setScale(1);
                                    });
                                this.sound.play('sfx-bonus-cards-turn_combine');
                                this.bonusGameItems[1].closeTween = this.tweens.add({
                                    targets: [this.bonusGameItems[1].flip_down],
                                    x: (this.alignGrid.getIndexPos(200).x / 2) - this.bonusGameX,
                                    duration: 1700,
                                    ease: 'Circ',
                                    onComplete: () => {
                                        this.bonusGameItems[1].spreadTween = this.tweens.add({
                                           targets: [this.bonusGameItems[1].flip_down],
                                           x: ((this.alignGrid.getIndexPos(190).x * -1) / 2) + this.bonusGameXSecond,
                                           ease: 'Quad',
                                           duration: 400,
                                            onComplete: () => {
                                                this.bonusGameItems[1].flip_down.setAlpha(0);
                                                this.bonusGameItems[1].setAlpha(1);
                                                this.bonusGameItems[1].group.iterate(child => {
                                                 child.x = this.bonusGameItems[1].flip_down.x
                                              });
                                            },
                                        });
                                    }
                                    }
                                );

                                this.bonusGameItems[2].setInteractive({cursor: 'pointer'})
                                    .on('pointerup', () => {
                                        if (this.bonusCardRevealed || !this.bonusGamePlayable) {
                                            return;
                                        }

                                        this.revealBonusCard(this.bonusGameItems[2], callback, 2, item);
                                    })
                                    .on('pointerover', () => {
                                        if (this.bonusCardRevealed || !this.bonusGamePlayable) {
                                            return;
                                        }

                                        this.bonusGameItems[2].setScale(0.95);
                                    })
                                    .on('pointerout', () => {
                                        if (this.bonusCardRevealed || !this.bonusGamePlayable) {
                                            return;
                                        }

                                        this.bonusGameItems[2].setScale(1);
                                    });

                                this.bonusGameItems[2].closeTween = this.tweens.add({
                                    targets: [this.bonusGameItems[2].flip_down],
                                    scale: 0.8,
                                    duration: 1500,
                                    ease: 'Circ',
                                    onComplete: () => {
                                        this.bonusGameItems[2].flip_down.setAlpha(0);
                                        this.bonusGameItems[2].setAlpha(1);
                                    }
                                });

                                this.bonusGameItems[3].setInteractive({cursor: 'pointer'})
                                    .on('pointerup', () => {
                                        if (this.bonusCardRevealed || !this.bonusGamePlayable) {
                                            return;
                                        }

                                        this.revealBonusCard(this.bonusGameItems[3], callback, 3, item);
                                    })
                                    .on('pointerover', () => {
                                        if (this.bonusCardRevealed || !this.bonusGamePlayable) {
                                            return;
                                        }

                                        this.bonusGameItems[3].setScale(0.95);
                                    })
                                    .on('pointerout', () => {
                                        if (this.bonusCardRevealed || !this.bonusGamePlayable) {
                                            return;
                                        }

                                        this.bonusGameItems[3].setScale(1);
                                    });

                                this.bonusGameItems[3].closeTween = this.tweens.add({
                                    targets: [this.bonusGameItems[3].flip_down],
                                    x: ((this.alignGrid.getIndexPos(200).x * -1) / 2) + this.bonusGameX,
                                    duration: 1700,
                                    ease: 'Circ',
                                    onComplete: () => {
                                        this.bonusGameItems[3].spreadTween = this.tweens.add({
                                            targets: [this.bonusGameItems[3].flip_down],
                                            x: (this.alignGrid.getIndexPos(190).x / 2) - this.bonusGameXSecond,
                                            ease: 'Quad',
                                            duration: 400,
                                            onComplete: () => {
                                                this.bonusGameItems[3].flip_down.setAlpha(0);
                                                this.bonusGameItems[3].setAlpha(1);
                                                this.bonusGameItems[3].group.iterate(child => {
                                                    child.x = this.bonusGameItems[3].flip_down.x
                                                });
                                                this.bonusGamePlayable = true;
                                            },
                                        });
                                    }
                                });
                            });

                        this.time.delayedCall(1700, () => {
                            if (!this.stopSmoke) {
                                this.bonusGameItems[4].setAlpha(1);
                                this.sound.play('sfx-bonus-cards-mix_spread');
                                this.bonusGameItems[4].play('dust_cloud').once('oncomplete', () => {
                                    this.bonusGameItems[4].setAlpha(0);
                                })
                            }
                        })
                    })
                })
            }
        });
    }

    revealBonusCard(item, callback, emitterOffset, symbolItem) {
        this.bonusCardRevealed = true;
        this.bonusSecondStage = false;

        this.sound.play('sfx-click-card-0' + Phaser.Math.RND.integerInRange(1, 3));

        for (let i = 1; i <= 3; i++) {
            if (i === item.index) {
                if (item.isLucky) {
                    continue;
                }

                let temp = Object.values(this.bonusGameItems).find(el => el.isLucky === true);
                temp.number.setText(item.number.amount);
                continue;
            }

            this.tweens.add({
                targets: [this.bonusGameItems[i]],
                scale: 0.9,
                alpha: 0.7,
                duration: 300,
                ease: 'Power0',
                onComplete: () => {
                  this.bonusGameItems[i].winNumber.setScale(0).setAlpha(0);
                  this.bonusGameItems[i].flip_up.setScale(0.9);
                },
            })
        }

        item.setAlpha(0);
        item.flip_up.setAlpha(1).play('bonus_card_flip_up').once('animationcomplete', () => {
            this.sound.play('sfx-bonus-card-win');
           item.winNumber.setAlpha(1);
           this.tweens.add({
              targets: [item.winNumber],
              scale: 1,
              duration: 300,
              ease: 'Power0',
               onComplete: () => {
                item.flip_up.setAlpha(0);

                if (item.moneyParticle) {
                    item.moneyParticle.destroy();
                }

                item.moneyParticle = this.add.particles('coin_loop');
                let offsetXOne = 0;
                let offsetXTwo = 50;

                if (emitterOffset === 1) {
                    offsetXOne = 100;
                    offsetXTwo = 20;
                } else if (emitterOffset === 3) {
                    offsetXOne = 0;
                    offsetXTwo = 50;
                }

                item.moneyEmitter = item.moneyParticle.createEmitter({
                    x: { min: item.group.x - offsetXOne, max: item.group.x + offsetXTwo },
                    y: (item.group.y / 2),
                    lifespan: 2000,
                    speedY: { min: 100, max: -500 },
                    speedX: { min: -300, max: 300 },
                    particleClass: AnimatedParticle,
                    gravityY: 1000,
                    quantity: 1,
                    on: false
                });

                item.moneyEmitter.explode(50);

                 this.sound.play('sfx-magic-coins-0' + Phaser.Math.RND.integerInRange(1, 3));
                 item.win.setAlpha(1).play('bonus_win').once('animationcomplete', () => {
                     this.bonusEnd = true;
                     this.winning += this.request.scenario.bonus.symbols.find(el => el.win === true).amount;

                     store.commit('setAmountWon', this.winning);

                     item.win.setAlpha(0);
                     item.flip_up.setAlpha(1);

                     for (let i = 1; i <= 3; i++) {
                         if (i === item.index) {
                             continue;
                         }

                         this.time.delayedCall(150 + (i * 100), () => {
                            this.bonusGameItems[i].setAlpha(0)
                             this.bonusGameItems[i].flip_up.setAlpha(0.8).play('bonus_card_flip_up').once('animationcomplete', () => {
                                this.tweens.add({
                                    targets: [this.bonusGameItems[i].number],
                                    scale: 0.8,
                                    alpha: 0.7,
                                    duration: 300,
                                    ease: 'Power0',
                                })
                             });
                         });
                     }

                    this.sound.play('sfx-bonus-card-reveal_disapppear');
                     this.time.delayedCall(2000, () => {
                         for (let i = 1; i <= 3; i++) {
                             this.time.delayedCall(150 + (i * 200), () => {
                                this.bonusGameItems[i].tweenDown = this.tweens.add({
                                   targets: [this.bonusGameItems[i].flip_up, this.bonusGameItems[i].win, this.bonusGameItems[i].flip_down, this.bonusGameItems[i].number, this.bonusGameItems[i], this.bonusGameItems[i].winNumber],
                                   y: this.bonusGameItems[i].flip_up.y + (this.alignGrid.getIndexPos(200).y) + 150,
                                   duration: 300,
                                   ease: 'Power0',
                                });
                             });
                         }

                         this.time.delayedCall(1000, () => {
                            this.playRandomValuePoint();
                            this.tweens.add({
                                targets: [this.shadow],
                                alpha: 0,
                                duration: 300,
                                ease: 'Power0',
                                onComplete: () => {
                                    let bonusCard = Object.values(this.mainGameItems.your_symbols).find(el => el.symbol.name === 'star');
                                    this.tweens.add({
                                        targets: [bonusCard.symbol.number],
                                        scale: 1,
                                        duration: 300,
                                        ease: 'Power0'
                                    });
                                    this.customHelper.removeClickable(this.bonusGameItems[1]);
                                    this.customHelper.removeClickable(this.bonusGameItems[2]);
                                    this.customHelper.removeClickable(this.bonusGameItems[3]);
                                    this.bonusModeStarted = false;
                                    this.bonusCardRevealed = false;

                                    if (!this.openedYourSymbols.includes(symbolItem.symbol.place)) {
                                        this.openedYourSymbols.push(symbolItem.symbol.place);
                                    }

                                    delete this.unopenedSymbols[symbolItem.symbol.place];
                                    this.openingRowsItems[symbolItem.row]++;
                                    this.openedRowsItems[symbolItem.row]++;
                                    this.checkForContinueGame(symbolItem);

                                    if (callback) {
                                        callback();
                                    } else {
                                        this.enablOpenAll();
                                    }
                                }
                            })
                         });
                     })
                 })
               },
           });
        });
    }

    showTwins(item, swapCards = false, callback) {
        if(item.opened || (this.bonusModeStarted) || this.shotsFired) {
            return false;
        }

        store.commit('disableOpenAllButton');

        this.twinsBeingRevealed = true;
        this.customHelper.removeClickable(item);
        item.opened = true;
        this.openingRowsItems[item.row]++;
        this.openedRowsItems[item.row]++;

        let keys = Object.keys(this.unopenedSymbols).sort(() => Math.random() - 0.5);
        let number = item.symbol.index;

        for (let i = 0; i < number; i++) {
            if (this.mainGameItems['your_symbols'][keys[i]].symbol.name === 'star') {
                number += 1;
                continue;
            }

            this.mainGameItems['your_symbols'][keys[i]].twinBeingRevealed = true;
            this.mainGameItems['your_symbols'][keys[i]].twinsCover.twinBeingRevealed = true;
        }

        item.setAlpha(0);
        item.flip_down.setAlpha(1);
        item.flip_down.play(`regular_cover_flip_down`);
        item.flip_down.once('animationcomplete', () => {
            item.flip_down.setAlpha(0);
            item.symbol.flip_up.setAlpha(1);
            item.symbol.flip_up.play(`regular_twins${item.symbol.index}_flip_up`);
            item.symbol.flip_up.once('animationcomplete', () => {
                item.symbol.flip_up.setAlpha(0);

                item.symbol.winAnimation.setAlpha(1).play(`twins_${item.symbol.index}_win`).once('animationcomplete', () => {
                    item.symbol.winAnimation.setAlpha(0);
                    item.symbol.setAlpha(1);
                });

                number = item.symbol.index;

                for (let i = 0; i < number; i++) {
                    if (this.mainGameItems['your_symbols'][keys[i]].symbol.name === 'star') {
                        number += 1;
                        continue;
                    }

                    this.flyItem(item, i, keys, number, callback);
                }
            });
        });

        this.symbolIndex++;
        this.shotsFired = true;
    }

    flyItem(item, i, keys, number, callback) {
        let lastShot = false;

        this.sound.play('sfx-magic-drop');
        this.flyParticleTo(item, this.mainGameItems['your_symbols'][keys[i]], () => {
            this.mainGameItems['your_symbols'][keys[i]].shine.stop();
            this.mainGameItems['your_symbols'][keys[i]].shine.setAlpha(0);

            this.mainGameItems['your_symbols'][keys[i]].symbol.impact.setAlpha(1).play('impact').once('animationcomplete', () => {
                this.mainGameItems['your_symbols'][keys[i]].symbol.impact.setAlpha(0);
            });

            this.mainGameItems['your_symbols'][keys[i]].setAlpha(0);

            this.sound.play('sfx-drop-upgrade-0' + Phaser.Math.RND.integerInRange(1, 2));
            this.mainGameItems['your_symbols'][keys[i]].flip_down.setAlpha(1).play('regular_cover_flip_down').once('animationcomplete', () => {
                this.mainGameItems['your_symbols'][keys[i]].flip_down.setAlpha(0);
                this.mainGameItems['your_symbols'][keys[i]].flip_up.setTexture('regular_cover_twins_flip_up').setAlpha(1).play('regular_cover_twins_flip_up').once('animationcomplete', () => {
                    this.mainGameItems['your_symbols'][keys[i]].flip_up.setAlpha(0);
                    this.mainGameItems['your_symbols'][keys[i]].twinsCover.shine.setAlpha(1).play('cover_twins_shine').once('animationcomplete', () => {
                        this.mainGameItems['your_symbols'][keys[i]].twinsCover.shine.setAlpha(0);
                        this.mainGameItems['your_symbols'][keys[i]].setTexture('cover_twins').setAlpha(1);
                        this.mainGameItems['your_symbols'][keys[i]].isTwin = true;
                        this.mainGameItems['your_symbols'][keys[i]].twinBeingRevealed = false;
                        this.mainGameItems['your_symbols'][keys[i]].twinsCover.twinBeingRevealed = false;
                        this.twinsBeingRevealed = false;

                        if (i === number - 1) {
                            lastShot = true;
                        }

                        if (lastShot) {
                            if (callback) {
                                callback();
                            } else {
                                this.enablOpenAll();
                            }

                            this.checkForContinueGame(item);
                        }
                    });
                });
            });
        });
    }

    loadBonus() {

    }

    itemGoDown(item) {

    }

    enableIdleTimer() {
        this.idleTimer = this.time.addEvent({
            delay: 10000,
            callback: this.shakeUnopenedSymbols,
            callbackScope: this,
            loop: true
        });
    }

    shakeUnopenedSymbols() {
        for (let i = 1; i <= 15; i++) {
            let item = this.mainGameItems['your_symbols'][i];

            this.time.delayedCall(100 + (i * 50), () => {
                if (item.opened || item.hovered || this.twinsBeingRevealed || this.openingAll || this.actionStarted) {
                    return;
                }

                if (!this.globalTwinsOn) {
                    if (item.isTwin) {
                        item.twinsCover.shine.setInteractive({cursor: 'pointer'}).on('pointerover', () => {
                            item.twinsCover.shine.setAlpha(0);
                            item.twinsCover.setTexture('cover_twins_mouseover');
                            this.customHelper.removeClickable(item.twinsCover.shine);
                        }).on('pointerout', () => {
                            item.twinsCover.shine.setAlpha(0);
                            item.twinsCover.setTexture('cover_twins');
                            this.customHelper.removeClickable(item.twinsCover.shine);
                        });
                        item.twinsCover.shine.setAlpha(1).play('cover_twins_shine').once('animationcomplete', () => {
                            item.twinsCover.shine.setAlpha(0);
                        });
                    } else if (!item.isTwin) {
                        item.shine.setInteractive({cursor: 'pointer'}).on('pointerover', () => {
                            item.shine.setAlpha(0);
                            item.setTexture('cover_mouseover');
                            this.customHelper.removeClickable(item.shine);
                        }).on('pointerout', () => {
                            item.shine.setAlpha(0);
                            item.setTexture('cover');
                            this.customHelper.removeClickable(item.shine);
                        });

                        item.shine.setAlpha(1).play('cover_shine').once('animationcomplete', () => {
                            item.shine.setAlpha(0);
                        });
                    }
                }
            });
        }
    }

    resetIdleTimer() {
        this.stopIdleTimer();
        this.enableIdleTimer();
    }

    stopIdleTimer() {
        if (this.idleTimer) {
            this.idleTimer.remove();
            this.time.removeEvent(this.idleTimer);
        }
    }


    openLuckySymbol(item, ignoreTwins = false) {
        if((item.opened || (this.bonusModeStarted) || this.twinMode) && (!ignoreTwins || item.isTwin)) {
            return false;
        }

        this.sound.play('sfx-value-0' + Phaser.Math.RND.integerInRange(1, 5));

        this.symbolIndex++;
        this.customHelper.removeClickable(item);
        item.opened = true;

        item.setAlpha(0);
        item.flip_down.setAlpha(1);
        item.flip_down.play(`regular_cover_flip_down`);
        item.flip_down.once('animationcomplete', () => {
            item.flip_down.setAlpha(0);
            item.symbol.flip_up.setAlpha(1);
            item.symbol.flip_up.play(`regular_${item.symbol.index}_flip_up`);
            item.symbol.flip_up.once('animationcomplete', () => {
                item.symbol.flip_up.setAlpha(0);
                item.symbol.setAlpha(1);
                item.symbol.winAnimation.setAlpha(1);
                item.symbol.number.setText(item.symbol.amount).setAlpha(1).setStyle(this.fonts.winYellow);
                item.symbol.number.setFill(this.createGradientGold(item.symbol.number));
                item.symbol.number.setScale(0);
                this.customHelper.applyDynamicFontSize(item.symbol.number, 155);
                item.symbol.number.setScale(1);
                this.tweens.add({
                    targets: [item.symbol.number],
                    scale: 1,
                    duration: 200,
                    ease: 'Power0',
                    onComplete: () => {
                    }
                });

                this.playRandomValuePoint();

                let luckySymbol = Object.values(this.mainGameItems['lucky_symbols']).find(el => el.symbol.index === item.symbol.index);
                luckySymbol.symbol.winAnimation.setAlpha(1);
                luckySymbol.symbol.winAnimation.play(`gold_${luckySymbol.symbol.index}_win`);
                this.winning += item.symbol.amount;
                store.commit('setAmountWon', this.winning);

                if (item.moneyParticle) {
                    item.moneyParticle.destroy();
                }

                item.moneyParticle = this.add.particles('coin_loop');
                item.moneyEmitter = item.moneyParticle.createEmitter({
                    x: { min: item.cardGroup.x - 10 ,max: item.cardGroup.x + 10 },
                    y: item.cardGroup.y,
                    lifespan: 2000,
                    speedY: { min: 100, max: -500 },
                    speedX: { min: -300, max: 300 },
                    particleClass: AnimatedParticle,
                    gravityY: 1000,
                    quantity: 1,
                    on: false
                });

                item.moneyEmitter.explode(30);
                
                this.sound.play('sfx-magic-coins-0' + Phaser.Math.RND.integerInRange(1, 3));
                item.symbol.winAnimation.play(`gold_${item.symbol.index}_win`)
                item.symbol.winAnimation.once('animationcomplete', () => {
                    item.symbol.setAlpha(0);
                    item.symbol.winAnimation.setAlpha(0);
                    luckySymbol.symbol.winAnimation.setAlpha(0);
                    item.symbol.winSymbol.setAlpha(1);
                    this.openingRowsItems[item.row]++;
                    this.openedRowsItems[item.row]++;
                    this.checkForContinueGame(item);
                });
            });
        });
    }

    playRandomValuePoint() {
        this.sound.play('sfx-value-point-0' + Phaser.Math.RND.integerInRange(1, 5), {volume:0.7});
    }

    openYourSymbol(item, ignoreTwins = false) {
        if((item.opened || (this.bonusModeStarted) || this.twinMode || item.isTwin) && (!ignoreTwins || item.isTwin)) {
            return false;
        }

        this.sound.play('sfx-value-0' + Phaser.Math.RND.integerInRange(1, 5));

        this.symbolIndex++;
        this.customHelper.removeClickable(item);
        item.opened = true;

        item.setAlpha(0);
        item.flip_down.setAlpha(1);
        item.flip_down.play(`regular_cover_flip_down`);
        item.flip_down.once('animationcomplete', () => {
            item.flip_down.setAlpha(0);
            item.symbol.flip_up.setAlpha(1);
            item.symbol.flip_up.play(`regular_${item.symbol.index}_flip_up`);
            item.symbol.flip_up.once('animationcomplete', () => {
                item.symbol.flip_up.setAlpha(0);
                item.symbol.number.setFill(this.createGradientSilver(item.symbol.number));

                item.symbol.number.setText(item.symbol.amount).setAlpha(1).setScale(1);
                this.customHelper.applyDynamicFontSize(item.symbol.number, 155);
                item.symbol.number.setScale(0);
                this.playRandomValuePoint();
                this.tweens.add({
                    targets: [item.symbol.number],
                    scale: 1,
                    duration: 70,
                    ease: 'Power0',
                    onComplete: () => {
                        this.tweens.add({
                            targets: [item.symbol.lost],
                            alpha: 1,
                            duration: 200,
                            onComplete: () => {
                                item.symbol.setAlpha(0);
                                this.openingRowsItems[item.row]++;
                                this.openedRowsItems[item.row]++;
                                this.checkForContinueGame(item);
                            }
                        })
                    }
                });
                item.symbol.setAlpha(1);
            });
        });
    }

    openBonus(index, item) {

    }

    openTwins(index, item) {

    }

    afterPurchaseAnimation() {
        if(this.request.gamePlayedTimes > 0) {
            this.resetGame();
        } else {
            this.sound.play('sfx-shuffle-gold');
            for (let i = 1; i <= 5; i++) {
                this.time.delayedCall(i * 40, () => {
                    this.mainGameItems['lucky_symbols'][i].setAlpha(0);
                    this.mainGameItems['lucky_symbols'][i].flip_down.setAlpha(1).play('gold_cover_flip_down');
                    this.mainGameItems['lucky_symbols'][i].flip_down.once('animationcomplete', () => {
                        this.mainGameItems['lucky_symbols'][i].flip_down.setAlpha(0);
                        this.mainGameItems['lucky_symbols'][i].flip_up.setAlpha(1).play('gold_cover_flip_up');
                        this.mainGameItems['lucky_symbols'][i].flip_up.once('animationcomplete', () => {
                            this.mainGameItems['lucky_symbols'][i].flip_up.setAlpha(0);
                            this.mainGameItems['lucky_symbols'][i].setAlpha(1);
                        });
                    });
                });
            }

            this.sound.play('sfx-shuffle-big');
            for (let i = 1; i <= 15; i++) {
                if (i <= 5) {
                    this.time.delayedCall(i * 90, () => {
                        
                        this.mainGameItems['your_symbols'][i].setAlpha(0);
                        this.mainGameItems['your_symbols'][i].flip_down.setAlpha(1).play('regular_cover_flip_down');
                        this.mainGameItems['your_symbols'][i].flip_down.once('animationcomplete', () => {
                            this.mainGameItems['your_symbols'][i].flip_down.setAlpha(0);
                            this.mainGameItems['your_symbols'][i].flip_up.setAlpha(1).play('regular_cover_flip_up');
                            this.mainGameItems['your_symbols'][i].flip_up.once('animationcomplete', () => {
                                this.mainGameItems['your_symbols'][i].flip_up.setAlpha(0);
                                this.mainGameItems['your_symbols'][i].setAlpha(1);
                            });
                        });
                    });
                }

                if (i > 5 && i < 11) {
                    this.time.delayedCall(i * 30, () => {
                        this.mainGameItems['your_symbols'][i].setAlpha(0);
                        this.mainGameItems['your_symbols'][i].flip_down.setAlpha(1).play('regular_cover_flip_down');
                        this.mainGameItems['your_symbols'][i].flip_down.once('animationcomplete', () => {
                            this.mainGameItems['your_symbols'][i].flip_down.setAlpha(0);
                            this.mainGameItems['your_symbols'][i].flip_up.setAlpha(1).play('regular_cover_flip_up');
                            this.mainGameItems['your_symbols'][i].flip_up.once('animationcomplete', () => {
                                this.mainGameItems['your_symbols'][i].flip_up.setAlpha(0);
                                this.mainGameItems['your_symbols'][i].setAlpha(1);
                            });
                        });
                    });
                }

                if (i >= 11) {
                    this.time.delayedCall(i * 40, () => {
                        this.mainGameItems['your_symbols'][i].setAlpha(0);
                        this.mainGameItems['your_symbols'][i].flip_down.setAlpha(1).play('regular_cover_flip_down');
                        this.mainGameItems['your_symbols'][i].flip_down.once('animationcomplete', () => {
                            this.mainGameItems['your_symbols'][i].flip_down.setAlpha(0);
                            this.mainGameItems['your_symbols'][i].flip_up.setAlpha(1).play('regular_cover_flip_up');
                            this.mainGameItems['your_symbols'][i].flip_up.once('animationcomplete', () => {
                                this.mainGameItems['your_symbols'][i].flip_up.setAlpha(0);
                                this.mainGameItems['your_symbols'][i].setAlpha(1);
                            });
                        });
                    });
                }
            }
        }
    }

    flyStar(item, starsFound) {

    }

    resetGame() {
        this.cleanTwinRotations(() => {
            this.mainGameGroup.bringToTop(this.shadow);
            this.tweens.add({
                targets: [this.shadow],
                alpha: 0,
                duration: 300,
                ease: 'Power0'
            });

            for (let i = 1; i <= 3; i++) {
                    this.bonusGameItems[i].group.iterate((child) => {
                        child.alpha = 0;
                        child.scale = 1;
                    })

                this.bonusGameItems[i].isLucky = false;
            }

            this.sound.play('sfx-shuffle-gold');
            for (let i = 1; i <= 5; i++) {
                this.time.delayedCall(i * 40, () => {
                    this.mainGameItems['lucky_symbols'][i].symbol.setAlpha(0);
                    this.mainGameItems['lucky_symbols'][i].symbol.flip_down.setAlpha(1).play(`gold_${this.mainGameItems['lucky_symbols'][i].symbol.index}_flip_down`);
                    this.mainGameItems['lucky_symbols'][i].symbol.flip_down.once('animationcomplete', () => {
                        this.mainGameItems['lucky_symbols'][i].symbol.flip_down.setAlpha(0);
                        this.mainGameItems['lucky_symbols'][i].flip_up.setAlpha(1);
                        this.mainGameItems['lucky_symbols'][i].flip_up.play('gold_cover_flip_up').once('animationcomplete', () => {
                            this.mainGameItems['lucky_symbols'][i].flip_up.setAlpha(0);
                            this.mainGameItems['lucky_symbols'][i].setAlpha(1);
                        });
                    });
                });
            }

            this.sound.play('sfx-shuffle-big');
            for (let i = 1; i <= 15; i++) {

                this.time.delayedCall(i * 70, () => {
                    this.mainGameItems['your_symbols'][i].twinsCount = 0;
                    this.mainGameItems['your_symbols'][i].isTwin = false;
                    this.mainGameItems['your_symbols'][i].isClosing = false;
                    this.mainGameItems['your_symbols'][i].symbol.setAlpha(0);
                    this.mainGameItems['your_symbols'][i].symbol.lost.setAlpha(0);
                    this.mainGameItems['your_symbols'][i].twinsFrame.setAlpha(0);
                    this.mainGameItems['your_symbols'][i].symbol.multiplier = false;
                    this.mainGameItems['your_symbols'][i].twinsCover.symbol.multiplier = false;
                    this.mainGameItems['your_symbols'][i].twinsGroup.iterate((child) => {
                        child.setAlpha(0);
                    });
                    this.mainGameItems['your_symbols'][i].opened = false;
                    this.mainGameItems['your_symbols'][i].symbol.winSymbol.setAlpha(0);
                    this.mainGameItems['your_symbols'][i].symbol.number.setAlpha(0);
                    this.mainGameItems['your_symbols'][i].twinsCover.symbol.number.setAlpha(0);

                    let bonusName = this.mainGameItems['your_symbols'][i].symbol.name === 'star';
                    
                    if (bonusName) {
                        this.mainGameItems['your_symbols'][i].symbol.flip_down.setAlpha(1).play('bonus_flip_down').once('animationcomplete', () => {
                            this.mainGameItems['your_symbols'][i].symbol.flip_down.setAlpha(0);
                            this.mainGameItems['your_symbols'][i].flip_up.setAlpha(1);
                            this.mainGameItems['your_symbols'][i].flip_up.setTexture('regular_cover_flip_up');
                            this.mainGameItems['your_symbols'][i].flip_up.play('regular_cover_flip_up').once('animationcomplete', () => {
                                this.mainGameItems['your_symbols'][i].flip_up.setAlpha(0);
                                this.mainGameItems['your_symbols'][i].setTexture('cover').setAlpha(1);
                            });
                        });
                    } else {
                        let name = this.mainGameItems['your_symbols'][i].symbol.win ? 'gold_' + this.mainGameItems['your_symbols'][i].symbol.index + '_flip_down' : 'regular_' + this.mainGameItems['your_symbols'][i].symbol.index + '_flip_down'
                        this.mainGameItems['your_symbols'][i].symbol.flip_down.setAlpha(1).play(name).once('animationcomplete', () => {
                            this.mainGameItems['your_symbols'][i].symbol.flip_down.setAlpha(0);
                            this.mainGameItems['your_symbols'][i].flip_up.setAlpha(1);
                            this.mainGameItems['your_symbols'][i].flip_up.play('regular_cover_flip_up').once('animationcomplete', () => {
                                this.mainGameItems['your_symbols'][i].flip_up.setAlpha(0);
                                this.mainGameItems['your_symbols'][i].setTexture('cover').setAlpha(1);
                            });
                        });
                    }
                });
            }
        });
    }

    ticketFinished() {
        store.commit('hideOpenAllButton');
        this.request.ticketFinished();
    }

    afterComplete() {
    }

    createBg() {
        this.bg = this.add.sprite(0, 0, 'bg_horizontal').setOrigin(0.5, 0);
        this.positionerContainer.add(this.bg);
        this.bgCreated = true;
    }

    createGameItems() {
        this.mainGameGroup = this.add.container();
        this.mainGameItems = {'lucky_symbols':{}, 'your_symbols':{}, 'bonus': {}, 'twins': {}};
        this.bonusGameItems = {};

        let config = {
            key: 'gold_cover_flip_down',
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers('gold_cover_flip_down', {
                start: 0,
                end: 4
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: 'gold_cover_flip_up',
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers('gold_cover_flip_up', {
                start: 0,
                end: 5,
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: 'regular_cover_flip_up',
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers('regular_cover_flip_up', {
                start: 0,
                end: 5,
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: 'regular_cover_flip_down',
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers('regular_cover_flip_down', {
                start: 0,
                end: 4,
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: 'regular_cover_twins_flip_down',
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers('regular_cover_twins_flip_down', {
                start: 0,
                end: 4,
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: 'regular_cover_twins_flip_up',
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers('regular_cover_twins_flip_up', {
                start: 0,
                end: 4,
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: 'cover_twins_shine',
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers('cover_twins_shine', {
                start: 0,
                end: 24,
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: 'cover_shine',
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers('cover_shine', {
                start: 0,
                end: 24,
            }),

            frameRate: 25,
        };

        this.anims.create(config);

        config = {
            key: 'impact',
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers('impact', {
                start: 0,
                end: 23,
            }),

            frameRate: 12,
        };

        this.anims.create(config);

        let configDust = this.anims.create({
            key: 'dust_cloud',
            frames: this.anims.generateFrameNames('dust_cloud',
                {
                    prefix: 'Dust_Cloud_',
                    start: 1,
                    end: 58,
                    suffix: '.png',
                    zeroPad: 4
                }),
            frameRate: 25,
        });

        let configFlipDown = this.anims.create({
            key: 'bonus_card_flip_down',
            frames: this.anims.generateFrameNames('bonus_card_flip_down',
                {
                    prefix: 'Bonus_Card_Flip_DOWN_',
                    start: 0,
                    end: 12,
                    suffix: '.png',
                    zeroPad: 4
                }),
            frameRate: 25,
        });

        let configFlipUp = this.anims.create({
            key: 'bonus_card_flip_up',
            frames: this.anims.generateFrameNames('bonus_card_flip_up',
                {
                    prefix: 'Bonus_Card_Flip_UP_',
                    start: 0,
                    end: 12,
                    suffix: '.png',
                    zeroPad: 4
                }),
            frameRate: 25,
        });

        let configBonusWin = this.anims.create({
            key: 'bonus_win',
            frames: this.anims.generateFrameNames('bonus_win',
                {
                    prefix: 'Bonus_Card_Win_',
                    start: 0,
                    end: 25,
                    suffix: '.png',
                    zeroPad: 4,
                }),
            frameRate: 25,
        });

        let bigWinRaysConfig = this.anims.create({
            key: 'big_win_rays',
            frames: this.anims.generateFrameNames('big_win_rays',
                {
                    prefix: 'BigWin_Rays_',
                    start: 1,
                    end: 12,
                    suffix: '.png',
                    zeroPad: 4
                }),
            frameRate: 25,
        });

        this.bonusGroup = this.add.container();

        for (let i = 1; i <= 3; i++) {
            let bonusItem = this.add.sprite(0, 0, 'bonus_card_down').setOrigin(0.5, 0.5).setAlpha(0);
            bonusItem.group = this.add.container();
            bonusItem.flip_up = this.add.sprite(0, 0, 'bonus_card_flip_up').setOrigin(0.5, 0.5).setAlpha(0);
            bonusItem.flip_down = this.add.sprite(0, 0, 'bonus_card_flip_down').setOrigin(0.5, 0.5).setAlpha(0);
            bonusItem.flip_up.anims.load(configFlipUp);
            bonusItem.flip_up.setFrame('Bonus_Card_Flip_UP_0000.png');
            bonusItem.flip_down.anims.load(configFlipDown);
            bonusItem.flip_down.setFrame('Bonus_Card_Flip_DOWN_0000.png');
            bonusItem.win = this.add.sprite(0, 0, 'bonus_win').setOrigin(0.5, 0.5).setAlpha(0);
            bonusItem.win.anims.load(configBonusWin);
            bonusItem.win.setFrame('Bonus_Card_Win_0000.png');
            bonusItem.number = this.add.text(0, 0, '1.00', this.fonts.winYellowBonus).setOrigin(0.5, 0.5).setAlpha(0);
            bonusItem.winNumber = this.add.text(0, 0, '1.00', this.fonts.winYellowBonus).setOrigin(0.5, 0.5).setAlpha(0);
            bonusItem.isLucky = false;
            bonusItem.index = i;

            bonusItem.group.add(bonusItem);
            bonusItem.group.add(bonusItem.win);
            bonusItem.group.add(bonusItem.flip_up);
            bonusItem.group.add(bonusItem.flip_down);
            bonusItem.group.add(bonusItem.number);
            bonusItem.group.add(bonusItem.winNumber);
            this.bonusGroup.add(bonusItem.group);
            this.bonusGameItems[i] = bonusItem;
        }

        let dustCloud = this.add.sprite(0, 0, 'dust_cloud').setOrigin(0.5, 0,5).setAlpha(0);
        dustCloud.anims.load(configDust);
        dustCloud.setFrame('Dust_Cloud_0001.png');
        dustCloud.isLucky = false;

        this.bonusGameItems[4] = dustCloud;
        this.bonusGroup.add(dustCloud);
        this.mainGameGroup.add(this.bonusGroup);

        for (let i = 1; i <= 5; i++) {
            let item = this.add.sprite(0, 0, 'gold_cover').setOrigin(0.5, 0.5).setAlpha(1);
            item.flip_up = this.add.sprite(0, 0, 'gold_cover_flip_up').setOrigin(0.5, 0.5).setAlpha(0);
            item.flip_down = this.add.sprite(0, 0, 'gold_cover_flip_down').setOrigin(0.5, 0.5).setAlpha(0);
            item.symbol = this.add.sprite(0,0, 'gold_2').setOrigin(0.5, 0.5).setAlpha(0);
            item.symbol.flip_up = this.add.sprite(0, 0, 'gold_2_flip_up').setOrigin(0.5, 0.5).setAlpha(0);
            item.symbol.flip_down = this.add.sprite(0, 0, 'gold_2_flip_down').setOrigin(0.5, 0.5).setAlpha(0);
            item.symbol.winAnimation = this.add.sprite(0, 0, 'gold_2_win').setOrigin(0.5, 0.5).setAlpha(0);

            this.mainGameGroup.add(item);
            this.mainGameGroup.add(item.flip_up);
            this.mainGameGroup.add(item.flip_down);
            this.mainGameGroup.add(item.symbol);
            this.mainGameGroup.add(item.symbol.flip_up);
            this.mainGameGroup.add(item.symbol.flip_down);
            this.mainGameGroup.add(item.symbol.winAnimation);
            this.children.bringToTop(item);
            this.mainGameItems['lucky_symbols'][i] = item;
        }

        this.gameItemsRows = [];
        let rowIndex = 1;

        for (let i = 1; i <= 15; i++) {
            let item = this.add.sprite(0, 0, 'cover').setOrigin(0.5, 0.5).setAlpha(1)
            item.twinsCount = 0;
            item.cardGroup = this.add.container();
            item.twinsGroup = this.add.container();
            item.shine = this.add.sprite(0, 0, 'cover_shine').setOrigin(0.5, 0.5).setAlpha(0);
            item.twinsCover = this.add.sprite(0, 0, 'cover_twins').setOrigin(0.5, 0.5).setAlpha(0);
            item.twinsCover.shine = this.add.sprite(0, 0, 'cover_twins_shine').setOrigin(0.5, 0.5).setAlpha(0);
            item.twins_flip_up = this.add.sprite(0, 0, 'regular_cover_twins_flip_up').setOrigin(0.5, 0.5).setAlpha(0);
            item.twins_flip_down = this.add.sprite(0, 0, 'regular_cover_twins_flip_down').setOrigin(0.5, 0.5).setAlpha(0);
            item.flip_up = this.add.sprite(0, 0, 'regular_cover_flip_up').setOrigin(0.5, 0.5).setAlpha(0);
            item.flip_down = this.add.sprite(0, 0, 'regular_cover_flip_down').setOrigin(0.5, 0.5).setAlpha(0);
            item.symbol = this.add.sprite(0,0, 'regular_2').setOrigin(0.5, 0.5).setAlpha(0);
            item.symbol.lost = this.add.sprite(0, 0, 'lost_2').setOrigin(0.5, 0.5).setAlpha(0);
            item.symbol.impact = this.add.sprite(0, 0, 'impact').setOrigin(0.5, 0.5).setAlpha(0);
            item.symbol.flip_up = this.add.sprite(0, 0, 'regular_2_flip_up').setOrigin(0.5, 0.5).setAlpha(0);
            item.symbol.flip_down = this.add.sprite(0, 0, 'regular_2_flip_down').setOrigin(0.5, 0.5).setAlpha(0);
            item.win_flip_up = this.add.sprite(0, 0, 'gold_1_flip_up').setOrigin(0.5, 0.5).setAlpha(0);
            item.win_flip_down = this.add.sprite(0, 0, 'gold_1_flip_down').setOrigin(0.5, 0.5).setAlpha(0);
            item.symbol.winSymbol = this.add.sprite(0, 0, 'gold_2').setOrigin(0.5, 0.5).setAlpha(0);
            item.symbol.winAnimation = this.add.sprite(0, 0, 'gold_2_win').setOrigin(0.5, 0.5).setAlpha(0);
            item.symbol.coin = this.add.sprite(0,0, 'coin_loop').setOrigin(0.5, 0.5).setAlpha(0);
            item.symbol.multiplierAnimation = this.add.sprite(0, 0, 'regular_multiplier2_activate').setOrigin(0.5, 0.5).setAlpha(0);
            item.symbol.number = this.add.text(0, 45, '1.00', this.fonts.normalSilver).setOrigin(0.5, 0.5).setScale(0, 0).setAlpha(0);
            item.twinsCover.flip_up = this.add.sprite(0, 0, 'regular_cover_twins_flip_up').setOrigin(0.5, 0.5).setAlpha(0);
            item.twinsCover.flip_down = this.add.sprite(0, 0, 'regular_cover_twins_flip_down').setOrigin(0.5, 0.5).setAlpha(0);
            item.twinsCover.symbol = this.add.sprite(0, 0, 'regular_2').setOrigin(0.5, 0.5).setAlpha(0);
            item.twinsCover.symbol.impact = this.add.sprite(0, 0, 'impact').setOrigin(0.5, 0.5).setAlpha(0);
            item.twinsCover.symbol.lost = this.add.sprite(0, 0, 'lost_2').setOrigin(0.5, 0.5).setAlpha(0);
            item.twinsCover.symbol.flip_up = this.add.sprite(0, 0, 'regular_2_flip_up').setOrigin(0.5, 0.5).setAlpha(0);
            item.twinsCover.symbol.flip_down = this.add.sprite(0, 0, 'regular_2_flip_down').setOrigin(0.5, 0.5).setAlpha(0);
            item.twinsCover.symbol.winSymbol = this.add.sprite(0, 0, 'gold_2').setOrigin(0.5, 0.5).setAlpha(0);
            item.twinsCover.symbol.winAnimation = this.add.sprite(0, 0, 'gold_2_win').setOrigin(0.5, 0.5).setAlpha(0);
            item.twinsCover.symbol.coin = this.add.sprite(0,0, 'coin_loop').setOrigin(0.5, 0.5).setAlpha(0);
            item.twinsCover.symbol.multiplierAnimation = this.add.sprite(0, 0, 'regular_multiplier2_activate').setOrigin(0.5, 0.5).setAlpha(0);
            item.twinsCover.symbol.number = this.add.text(0, 45, '1.00', this.fonts.normalSilver).setOrigin(0.5, 0.5).setScale(0, 0).setAlpha(0);
            item.twinsFrame = this.add.sprite(0, 0, 'twins_frame').setOrigin(0.5, 0.5).setScale(0, 0).setAlpha(0);
            item.twinsCover.twinsFrame = this.add.sprite(0, 0, 'twins_frame').setOrigin(0.5, 0.5).setScale(0, 0).setAlpha(0);
            item.isTwin = false;
            item.row = rowIndex;
            item.i = i;

            if([5, 10, 15].includes(i)) {
                rowIndex++;
            }

            item.cardGroup.add(item);
            item.cardGroup.add(item.flip_up);
            item.cardGroup.add(item.flip_down);
            item.cardGroup.add(item.twins_flip_down);
            item.cardGroup.add(item.twins_flip_up);
            item.cardGroup.add(item.symbol);
            item.cardGroup.add(item.symbol.lost);
            item.cardGroup.add(item.symbol.impact);
            item.cardGroup.add(item.symbol.flip_up);
            item.cardGroup.add(item.symbol.flip_down);
            item.cardGroup.add(item.symbol.winSymbol);
            item.cardGroup.add(item.win_flip_up);
            item.cardGroup.add(item.win_flip_down);
            item.cardGroup.add(item.symbol.winAnimation);
            item.cardGroup.add(item.symbol.coin);
            item.cardGroup.add(item.symbol.multiplierAnimation);
            item.cardGroup.add(item.symbol.number);
            item.cardGroup.add(item.shine);
            item.cardGroup.add(item.twinsFrame);

            item.twinsGroup.add(item.twinsCover);
            item.twinsGroup.add(item.twinsCover.flip_up);
            item.twinsGroup.add(item.twinsCover.flip_down);
            item.twinsGroup.add(item.twinsCover.symbol);
            item.twinsGroup.add(item.twinsCover.symbol.lost);
            item.twinsGroup.add(item.twinsCover.symbol.flip_up);
            item.twinsGroup.add(item.twinsCover.symbol.flip_down);
            item.twinsGroup.add(item.twinsCover.symbol.winSymbol);
            item.twinsGroup.add(item.twinsCover.symbol.winAnimation);
            item.twinsGroup.add(item.twinsCover.symbol.coin);
            item.twinsGroup.add(item.twinsCover.symbol.multiplierAnimation);
            item.twinsGroup.add(item.twinsCover.symbol.number);
            item.twinsGroup.add(item.twinsCover.symbol.impact);
            item.twinsGroup.add(item.twinsCover.shine);
            item.twinsGroup.add(item.twinsCover.twinsFrame);

            this.mainGameGroup.add(item.cardGroup);
            this.mainGameGroup.add(item.twinsGroup);
            this.mainGameGroup.sendToBack(item);

            this.mainGameItems['your_symbols'][i] = item;
        }

        this.logo = this.add.sprite(0, 0, 'logo').setOrigin(0.5, 0.5);
        this.frame = this.add.sprite(0, 0, 'bg_frame').setOrigin(0.5, 0.5);
        this.pigOne = this.add.sprite(0, 0, 'pig_1').setOrigin(0.5, 0.5);
        this.pigTwo = this.add.sprite(0, 0, 'pig_2').setOrigin(0.5, 0.5);
        this.railing = this.add.sprite(0, 0, 'railing').setOrigin(0.5, 0.5);
        this.lucky_symbols = this.add.sprite(0, 0, 'lucky_symbols_text').setOrigin(0.5, 0.5);
        this.shadow = this.add.graphics().fillStyle(0x000000, 1).fillRect(0, 0, 1920, 1920);
        this.bigWinRays = this.add.sprite(0, 0, 'big_win_rays').setOrigin(0.5, 0.5).setAlpha(0);
        this.bigWinRays.anims.load(bigWinRaysConfig);

        this.shadow.setAlpha(0);

        this.mainGameGroup.add(this.logo);
        this.mainGameGroup.add(this.frame);
        this.mainGameGroup.add(this.pigOne);
        this.mainGameGroup.add(this.pigTwo);
        this.mainGameGroup.add(this.railing);
        this.mainGameGroup.add(this.lucky_symbols);
        this.mainGameGroup.add(this.shadow);
        this.mainGameGroup.add(this.bigWinRays);

        this.itemsCreated = true;
    }

    /*tryPaths() {
        let from = this.mainGameItems['your_symbols'][1];

        for (let i = 1; i <= 15; i++) {
            if (i != 1) {
                let item = this.mainGameItems['your_symbols'][i];
                this.flyParticleTo(from, item);
            }
        }
    }*/

    flyParticleTo(from, to, callback) {
        let startX = from.x + from.cardGroup.x;
        let startY = from.y + from.cardGroup.y;
        let destinationX = to.x + to.cardGroup.x;
        let destinationY = to.y + to.cardGroup.y;

        let x2Distance = (destinationX > startX ? destinationX - startX : startX - destinationX) / 2;
        let y2Distance = (destinationY > startY ? destinationY - startY : startY - destinationY) - 200;

        let path = new Phaser.Curves.Path(startX, startY);
        let x2 = destinationX + (destinationX > startX  ? -(x2Distance) : x2Distance);
        let y2 = destinationY > startY ? destinationY - y2Distance : destinationY + y2Distance;

        let x3 = destinationX;
        let y3 = destinationY;

        path.cubicBezierTo(destinationX, destinationY, x2, y2, x3, y3);

        /*let graphics = this.add.graphics();
        graphics.clear();
        graphics.lineStyle(2, 0xffffff, 1);
        path.draw(graphics);*/

        //this.time.delayedCall(2000, () => {
        let distance = Phaser.Math.Distance.Between(startX, startY, destinationX, destinationY) / 4;
        
        if (distance > 100) {
            distance = 100;
        }

        let scaler = 0.4;
        let delayOffset = 0;
        let itemsCount = 30;
        let delayOffsetStep = 0.008 * distance;
        let delayStaticOffset = 0.07 * distance;
    
        for (var i = 0; i < itemsCount; i++)
        {
            this.addFollower(path, distance, scaler, delayOffset, delayStaticOffset, i, callback);

            delayOffset += delayOffsetStep;

            scaler -= (0.4 / 30);
        }
       // });
    }

    addFollower(path, distance, scaler, delayOffset, delayStaticOffset, i, callback) {
        var follower = this.add.follower(path, 100, 100 + (30 * i), 'flares', 'yellow');

        follower.setDepth(10);
        follower.setBlendMode(Phaser.BlendModes.ADD);
        follower.setScale(scaler);

        let speed = distance * 6;

        follower.startFollow({
            duration:speed,
            positionOnPath: true,
            ease: 'Linear',
            delay: i * delayStaticOffset - delayOffset,
            onComplete: () => {
                follower.destroy();

                if(i === 0) {
                    if(callback) {
                        callback();
                    }
                }
            }
        });
    }

    checkForContinueGame(item) {
        if (this.openedYourSymbols.length === 15) {
                if (this.finishingGame) {
                    return;
                }

                store.commit('disableOpenAllButton');
                this.finishingGame = true;

                this.time.delayedCall(1000, () => {
                    this.mainGameGroup.bringToTop(this.shadow);
                    this.tweens.add({
                       targets: [this.shadow],
                       alpha: 0.6,
                       duration: 300,
                       ease: 'Power0'
                    });

                    if(this.request.scenario.total_prize > 0) {
                        const ratio = this.request.scenario.total_prize / this.request.ticketAmount;

                        if(ratio >= Config.assets.ratios.big_win) {
                            this.showBigWin();
                        } else {
                            this.showRegularWin();
                        }
                    } else {
                        this.finishGame();
                    }
                });

                this.stopIdleTimer();
        } else {
            if (item && this.openingAll && this.openedRowsItems[item.row] === 5 && this.openingRowsItems[item.row] === 5) {
                if (!this.twinMode && !this.twinsBeingRevealed) {
                    this.enablOpenAll();
                }
            } else if (!this.openingAll) {
                store.commit('showOpenAllButton');
            }

            this.resetIdleTimer();
        }
    }

    enablOpenAll() {
        store.commit('showOpenAllButton');
        this.openingAll = false;
        this.openingRow = false;
    }

    cleanTwinRotations(callback) {
        for (let i = 1; i <= 15; i++) {
            let item = this.mainGameItems['your_symbols'][i];

            if (item.rotationToTwins) {
                item.rotationToTwins.remove();

                if (!item.placeSymbol) {
                    this.rotateToTwins(item, true);
                }
            }
        }
        
        this.time.delayedCall(50, () => {
            callback();
        });
    }

    finishGame() {
        if (this.ticketDone) {
            return;
        }

        this.tweens.add({
            targets:  this.mainSound,
            volume:  1,
            duration: 500
        });

        this.ticketDone = true;
        this.ticketFinished();
    }

    showRegularWin() { 
        if (this.winPlaying) {
            return;
        }

        this.regularWinDestroyed = false;

        this.winPlaying = true;

        this.regularWinContainer = this.add.container();
        this.regularWinBg = this.add.sprite(0, 0, 'light_base').setOrigin(0.5, 0.5).setScale(0).setAlpha(1);
        this.sum = this.customHelper.createWinningFromSprites(General.format_currency(this.request.scenario.total_prize).replace(Config.currency.symbol, ''), 1, '', 0).setScale(0).setAlpha(1);
        this.shape = new Phaser.Geom.Ellipse(0, 0, 700, 700);
        this.shapeDeath = new Phaser.Geom.Ellipse(0, 0, 250, 250);

        this.regularWinContainer.add(this.regularWinBg);
        this.regularWinContainer.add(this.sum);
        this.regularWinPositioner();
        this.regularWinContainer.setInteractive({ cursor: 'pointer'})
            .once('pointerup', () => {
                this.destroyRegularWin();
            });

        this.tweens.add({
            targets: [this.regularWinBg],
            scale: 0.8,
            duration: 200,
            ease: 'Power0',
        });

        this.sound.play('sfx-win');

        this.tweens.add({
           targets: [this.sum],
           scale: 1,
           duration: 200,
           ease: 'Power0',
           completeDelay: 1500,
           onComplete: () => {
               this.regularWinEmitter.stop();
               this.destroyRegularWin();
           }
        });

        this.time.delayedCall(400, () => {
            this.particle = this.add.particles('spark');
            this.regularWinEmitter = this.particle.createEmitter({
                x: { min: 0, max: 50 },
                y: { min: 0, max: -50},
                lifespan: 500,
                gravity: 0,
                quantity: 5,
                rotate: { start: 0, end: 360 },
                scale: { min: 0.2, max: 1},
                frequency: 300,
                blendMode: 'ADD',
                emitZone: { source: this.shape },
                deathZone: { type: 'onEnter', source: this.shapeDeath },
                maxParticles: 30,
            });
        });
    }

    regularWinPositioner() {
        if(!this.regularWinContainer) {
            return;
        }

        this.regularWinContainer.x = this.cameras.main.centerX;
        this.regularWinContainer.y = this.cameras.main.centerY;
        this.shape.x = this.cameras.main.centerX;
        this.shape.y = this.cameras.main.centerY;
        this.shapeDeath.x = this.cameras.main.centerX;
        this.shapeDeath.y = this.cameras.main.centerY;
        this.regularWinContainer.setSize(this.cameras.main.width, this.cameras.main.height);

        if (this.orientationIsSetToPortrait()) {
        } else {
        }
    }

    incrementTo(winning, callback) {
        let lastAmount = 0;
        let delay = 0;
        let scaleStep = 0;
        let scale = this.bigWinAmountContainer.scaleX;

        if(lastAmount === winning) {
            this.setAmountText(delay, winning, true, 1, callback)
        } else {
            if(!this.pointCountSound.isPlaying) {
                this.pointCountSound.play();
            }

            var incrementSize = winning / 45;

            if(incrementSize < 0.01) {
                incrementSize = 0.01;
            }

            if(parseFloat(incrementSize) > 1) {
                incrementSize = Math.ceil(incrementSize);
            }

            scaleStep = 0.5 / (winning / incrementSize);

            while(lastAmount !== winning) {
                lastAmount += incrementSize;

                if(lastAmount > winning) {
                    lastAmount = winning;
                }

                scale += scaleStep;

                if(scale > 1) {
                    scale = 1;
                }

                this.setAmountText(delay, lastAmount, lastAmount === winning, scale, callback)

                delay += 30;
            }
        }
    }

    setAmountText(delay, amount, last, scale, callback) {
        amount = General.format_currency(amount).replace(Config.currency.symbol, '');

        this.time.delayedCall(delay, () => {
            if (this.bigWinDestroyed) {
                return;
            }

            if (!this.stopBigWinIncrement) {
                this.bigWinAmountContainer.destroy();

                this.bigWinAmountContainer = this.customHelper.createWinningFromSprites(amount, 1, '', 0)
                    .setX(this.bigWinContainer.x + this.bigWin.x)
                    .setY(this.bigWinContainer.y + this.bigWin.y + this.bigWin.displayHeight / 2 + 80)
                    .setScale(scale);

                if(last) {
                    this.pointCountSound.stop();
                    this.sound.play('sfx-point-last');

                    this.tweens.add({
                        targets: this.bigWinAmountContainer,
                        scale: 1.1,
                        ease: 'Power0',
                        duration: 200,
                        yoyo: true,
                        onComplete: () => {
                            if(callback) {
                                callback();
                            }
                        }
                    });
                }
            }
        }, this);
    }

    destroyRegularWin() {
        if (this.regularWinDestroyed) {
            return;
        }

        this.regularWinDestroyed = true;

        this.customHelper.removeClickable(this.regularWinContainer);

        this.tweens.add({
            targets: [this.regularWinBg],
            scale: 0,
            duration: 400,
            ease: 'Power0',
        });

        this.tweens.add({
            targets: [this.sum],
            scale: 0,
            duration: 400,
            ease: 'Power0',
            onComplete: () => {
                this.regularWinBg.destroy();
                this.sum.destroy();
                this.particle.destroy();

                if (this.regularWinContainer) {
                    this.regularWinContainer.destroy();
                }

                this.winPlaying = false;
                this.finishGame();
            }
        });
    }

    showBigWin() {
        if (this.winPlaying) {
            return;
        }

        this.bigWinDestroyed = false;
        this.winPlaying = true;
        this.mainGameGroup.bringToTop(this.bigWinRays);
        this.bigWinContainer = this.add.container();
        this.shape = new Phaser.Geom.Ellipse(0, 0, 700, 700);
        this.shapeDeath = new Phaser.Geom.Ellipse(0, 0, 250, 250);
        this.bigWinBg = this.add.sprite(0, 0, 'light_base').setOrigin(0.5, 0.5).setScale(0).setAlpha(1);
        this.bigWin = this.add.sprite(0, 0, 'big_win_txt').setOrigin(0.5, 0.5).setScale(0.1).setAlpha(1);
        this.bigWinContainer.add(this.bigWinBg);
        this.bigWinContainer.add(this.bigWin);

        this.bigWinAmountContainer = this.customHelper.createWinningFromSprites(0.00, 1, '', -50)
            .setX(this.bigWinContainer.x + this.bigWin.x)
            .setY(this.bigWinContainer.y + this.bigWin.y + this.bigWin.displayHeight / 2 + 80);
        this.bigWinAmountContainer
            .setScale(0.7);
        this.bigWinContainer.add(this.bigWinAmountContainer);
        this.bigWinPositioner();
        this.bigWinContainer.setInteractive({ cursor: 'pointer'})
            .once('pointerup', () => {
                this.destroyBigWin();
            });

        this.sound.play('stg-bigwin');

        this.incrementTo(this.request.scenario.total_prize);
        this.bigWinRays.play({key: 'big_win_rays', repeat: 30});
        this.tweens.add({
            targets: [this.bigWinRays],
            alpha: 1,
            duration: 200,
            ease: 'Power4',
            completeDelay: 2500,
            onComplete: () => {
                this.tweens.add({
                    targets: [this.bigWinRays],
                    alpha: 0,
                    duration: 200,
                    ease: 'Power0',
                });
            }
        });

        this.tweens.add({
            targets: [this.bigWin],
            scale: 1,
            duration: 200,
            ease: 'Power4',
            completeDelay: 2500,
            onComplete: () => {
                this.tweens.add({
                    targets: [this.bigWin, this.bigWinAmountContainer],
                    scale: 0,
                    duration: 200,
                    ease: 'Power0',
                    onComplete: () => {
                            this.regularWinEmitter.stop();
                            this.moneyEmitter1.stop();
                            this.moneyEmitter2.stop();
                            this.moneyEmitter3.stop();
                            this.moneyEmitter4.stop();
                            this.destroyBigWin();
                    }
                });
            }
        });


        this.tweens.add({
            targets: [this.bigWinBg],
            scale: 0.8,
            duration: 200,
            ease: 'Power4',
            hold: 2500,
        });

            this.particle = this.add.particles('spark');
            this.regularWinEmitter = this.particle.createEmitter({
                x: { min: 0, max: 50 },
                y: { min: 0, max: 50 },
                lifespan: 2000,
                gravity: 0,
                quantity: 5,
                scale: { start: 1, end: 0},
                frequency: 300,
                blendMode: 'ADD',
                emitZone: { source: this.shape },
                deathZone: { type: 'onEnter', source: this.shapeDeath },
                maxParticles: 30,
            });

            this.moneyParticle1 = this.add.particles('money_1');
            this.moneyEmitter1 = this.moneyParticle1.createEmitter({
                x: { min: 0, max: this.cameras.main.width },
                y: {start: this.cameras.main.height, end: (Math.floor(Math.random() * 500) * -1)},
                lifespan: 2000,
                speedY: {min: -100, max: 0},
                gravity: 0,
                quantity: 5,
                scale: { start: 1, end: 0 },
                frequency: 300,
                blendMode: 'ADD',
                deathZone: { type: 'onEnter', source: this.shapeDeath },
                maxParticles: 30,
            });

            this.moneyParticle2 = this.add.particles('money_2');
            this.moneyEmitter2 = this.moneyParticle2.createEmitter({
                x: { min: 0, max: this.cameras.main.width },
                y: {start: this.cameras.main.height, end: (Math.floor(Math.random() * 500) * -1)},
                lifespan: 2000,
                speedY: {min: -300, max: 0},
                gravity: 0,
                quantity: 5,
                scale: { start: 1, end: 0 },
                frequency: 300,
                blendMode: 'ADD',
                deathZone: { type: 'onEnter', source: this.shapeDeath },
                maxParticles: 30,
            });

            this.moneyParticle3 = this.add.particles('money_3');
            this.moneyEmitter3 = this.moneyParticle3.createEmitter({
                x: { min: 0, max: this.cameras.main.width },
                y: {start: this.cameras.main.height, end: (Math.floor(Math.random() * 500) * -1)},
                lifespan: 2000,
                speedY: {min: -1000, max: 0},
                gravity: 0,
                quantity: 5,
                scale: { start: 1, end: 0 },
                frequency: 300,
                blendMode: 'ADD',
                deathZone: { type: 'onEnter', source: this.shapeDeath },
                maxParticles: 30,
            });

            this.moneyParticle4 = this.add.particles('money_4');
            this.moneyEmitter4 = this.moneyParticle4.createEmitter({
                x: { min: 0, max: this.cameras.main.width },
                y: {start: this.cameras.main.height, end: (Math.floor(Math.random() * 500) * -1)},
                lifespan: 2000,
                speedY: {min: -1000, max: 0},
                gravity: 0,
                quantity: 5,
                scale: { start: 1, end: 0 },
                frequency: 300,
                blendMode: 'ADD',
                deathZone: { type: 'onEnter', source: this.shapeDeath },
                maxParticles: 30,
            });
    }

    destroyBigWin() {
        if (this.bigWinDestroyed) {
            return;
        }

        this.bigWinDestroyed = true;

        if (this.stopBigWinIncrement || !this.winPlaying) {
            return;
        }

        this.stopBigWinIncrement = true;
        this.customHelper.removeClickable(this.bigWinContainer);

        this.tweens.add({
            targets: [this.bigWin, this.bigWinAmountContainer],
            scale: 0,
            duration: 200,
            ease: 'Power0',
            onComplete: () => {
                this.regularWinEmitter.stop();
                this.moneyEmitter1.stop();
                this.moneyEmitter2.stop();
                this.moneyEmitter3.stop();
                this.moneyEmitter4.stop();
                this.bigWin.destroy();

                if (this.bigWinAmountContainer) {
                    this.bigWinAmountContainer.destroy();
                }
            }
        });

        this.tweens.add({
            targets: [this.bigWinRays],
            alpha: 0,
            duration: 200,
            ease: 'Power0',
        });

        this.tweens.add({
            targets: [this.bigWinBg],
            scale: 0,
            duration: 200,
            ease: 'Power4',
            onComplete: () => {
                this.bigWinBg.destroy();
                this.particle.destroy();
                this.moneyParticle1.destroy();
                this.moneyParticle2.destroy();
                this.moneyParticle3.destroy();
                this.moneyParticle4.destroy();

                if (this.bigWinContainer) {
                    this.bigWinContainer.destroy();
                }

                this.finishGame();
            }
        });

    }

    bigWinPositioner() {
        if(!this.bigWinContainer) {
            return;
        }

        this.bigWinContainer.setX(this.cameras.main.centerX).setY(this.cameras.main.centerY);
        this.bigWin.y -= 50;

        if (this.bigWinAmountContainer) {
            this.bigWinAmountContainer
                .setX(this.bigWinContainer.x + this.bigWin.x)
                .setY(this.bigWinContainer.y + this.bigWin.y + this.bigWin.displayHeight / 2 + 80);
        }

        this.shape.x = this.cameras.main.centerX;
        this.shape.y = this.cameras.main.centerY;
        this.shapeDeath.x = this.cameras.main.centerX;
        this.shapeDeath.y = this.cameras.main.centerY;
        this.bigWinContainer.setSize(this.cameras.main.width, this.cameras.main.height);

        if (this.orientationIsSetToPortrait()) {
            this.bigWin.y += 50;

            if (this.bigWinAmountContainer) {
                this.bigWinAmountContainer
                    .setX(this.bigWinContainer.x + this.bigWin.x)
                    .setY(this.bigWinContainer.y + this.bigWin.y + this.bigWin.displayHeight / 2 + 130);
            }
        }
    }

    gamePositioner() {
        if(!this.spritesReady) {
            return;
        }

        if(this.alignGrid) {
            this.alignGrid.destroy();
            delete this.alignGrid;
        }

        this.alignGrid = new AlignGrid({
            'scene': this,
            'cols': 21,
            'rows': 21
        });

        if (this.orientationIsSetToPortrait()) {
            this.bonusGameX = 0;
            this.bonusGameXSecond = 35;
            this.bg.setTexture('bg_vertical');
            this.bigWinRays.angle = -90;
            this.pigOne.setScale(0.8, 0.8);
            this.pigTwo.setScale(0.8, 0.8);

            this.alignGrid.placeAtIndex(31, this.logo,0, 0);
            this.alignGrid.placeAtIndex(136, this.frame,10, 75);
            this.alignGrid.placeAtIndex(241, this.railing,10, -20);
            this.alignGrid.placeAtIndex(211, this.pigOne,10, -40);
            this.alignGrid.placeAtIndex(229, this.pigTwo,20, -40);
            this.alignGrid.placeAtIndex(115, this.lucky_symbols,20, 0);

            let startIndex = 0;
            let x = 43;
            let y = 10;
            let yAnims = 10;

            for (let i = 1; i <= 5; i++) {
                this.alignGrid.placeAtIndex(87 + startIndex, this.mainGameItems['lucky_symbols'][i], x, y);
                this.alignGrid.placeAtIndex(87 + startIndex, this.mainGameItems['lucky_symbols'][i].flip_up, x, yAnims);
                this.alignGrid.placeAtIndex(87 + startIndex, this.mainGameItems['lucky_symbols'][i].flip_down, x, yAnims);
                this.alignGrid.placeAtIndex(87 + startIndex, this.mainGameItems['lucky_symbols'][i].symbol, x, y);
                this.alignGrid.placeAtIndex(87 + startIndex, this.mainGameItems['lucky_symbols'][i].symbol.flip_down, x, yAnims);
                this.alignGrid.placeAtIndex(87 + startIndex, this.mainGameItems['lucky_symbols'][i].symbol.flip_up, x, yAnims);
                this.alignGrid.placeAtIndex(87 + startIndex, this.mainGameItems['lucky_symbols'][i].symbol.winAnimation, x, yAnims);
                startIndex += 3;
                x += 7;
            }

            let column = 129;
            startIndex = 0;
            x = 43;
            y = 0;

            for (let i = 1; i<= 15; i++) {
                if (i === 6) {
                    x = 43;
                    column = 171;
                    startIndex = 0;
                    y = -20;
                }

                if (i === 11) {
                    x = 43;
                    column = 213;
                    startIndex = 0;
                    y = -40;
                }

                this.alignGrid.placeAtIndex(column + startIndex, this.mainGameItems['your_symbols'][i].cardGroup, x, y);
                this.alignGrid.placeAtIndex(column + startIndex, this.mainGameItems['your_symbols'][i].twinsGroup, x, y);
                startIndex += 3;
                x += 7;
            }

            startIndex = 0;

            for (let i = 1; i <= 3; i++) {
                this.alignGrid.placeAtIndex(402 + startIndex, this.bonusGameItems[i].group, 0, 100);
                startIndex += 7;
            }

            this.alignGrid.placeAtIndex(200, this.bonusGameItems[4], -50, -400);

            if (this.bonusModeStarted) {
                startIndex = 0;

                for (let i = 1; i <= 3; i++) {
                    if (this.bonusGameItems[i].tween && this.bonusGameItems[i].tween.isPlaying()) {
                        this.bonusGameItems[i].tween.stop();
                        this.bonusGameItems[i].y = (this.alignGrid.getIndexPos(195 + startIndex).y * -1) - 50;

                        this.bonusGameItems[i].group.iterate((children) => {
                            children.y = this.bonusGameItems[i].y;
                        });
                    }

                    if (this.bonusGameItems[i].closeTween && this.bonusGameItems[i].closeTween.isPlaying()) {
                        this.stopSmoke = true;
                        this.bonusGameItems[i].closeTween.stop();

                        if (i === 2) {
                            this.bonusGameItems[i].flip_down.setScale(1);
                        }

                        this.bonusGameItems[i].flip_down.x = (this.alignGrid.getIndexPos(190).x / 2) - 40;
                        this.bonusGameItems[i].x = (this.alignGrid.getIndexPos(190).x / 2) - 40;
                        this.bonusGameItems[i].group.iterate(child => {
                            child.x = this.bonusGameItems[i].x;
                        });

                        this.bonusGameItems[i].flip_down.setAlpha(0);
                        this.bonusGameItems[i].setAlpha(1);
                    } else if (this.bonusSecondStage) {
                        this.bonusGameItems[i].flip_down.x = (this.alignGrid.getIndexPos(190).x / 2) - 40;
                        this.bonusGameItems[i].x = (this.alignGrid.getIndexPos(190).x / 2) - 40;
                        this.bonusGameItems[i].group.iterate(child => {
                            child.x = this.bonusGameItems[i].x
                        });

                        this.bonusGameItems[i].flip_down.setAlpha(0);
                        this.bonusGameItems[i].setAlpha(1);
                    }


                    this.bonusGameItems[i].y = (this.alignGrid.getIndexPos(195 + startIndex).y * -1) - 50;
                    this.bonusGameItems[i].group.iterate((children) => {
                        children.y = this.bonusGameItems[i].y;
                    });
                    startIndex += 5

                    if (this.bonusGameItems[4].isPlaying) {
                        this.bonusGameItems[4].stop();
                    }

                    if (this.bonusGameItems[i].tweenDown && this.bonusGameItems[i].tweenDown.isPlaying()) {
                        this.bonusGameItems[i].tweenDown.stop();
                        this.bonusGameItems[i].flip_up.y +=  (this.alignGrid.getIndexPos(200).y) + 150;
                        this.bonusGameItems[i].group.iterate((children) => {
                           children.y = this.bonusGameItems[i].flip_up.y;
                        });
                    } else if (this.bonusEnd) {
                        this.bonusGameItems[i].flip_up.y += (this.alignGrid.getIndexPos(200).y) + 150;
                        this.bonusGameItems[i].group.iterate((children) => {
                            children.y = this.bonusGameItems[i].flip_up.y;
                        });
                    }
                }
            }
        } else {
            this.bonusGameX = 100;
            this.bonusGameXSecond = 0;
            this.bigWinRays.angle = 0;
            this.bg.setTexture('bg_horizontal');

            this.pigOne.setScale(1, 1);
            this.pigTwo.setScale(1, 1);

            this.alignGrid.placeAtIndex(262, this.frame,20, -40);
            this.alignGrid.placeAtIndex(388, this.railing, 20, 25);
            this.alignGrid.placeAtIndex(341, this.pigOne,-5, 0);
            this.alignGrid.placeAtIndex(351, this.pigTwo,40, 0);
            this.alignGrid.placeAtIndex(73, this.logo,0, -40);
            this.alignGrid.placeAtIndex(157, this.lucky_symbols,20, 45);

            let startIndex = 0;
            let x = 60;
            let y = 16;
            let yAnims = 16;

            for (let i = 1; i <= 5; i++) {
                this.alignGrid.placeAtIndex(132 + startIndex, this.mainGameItems['lucky_symbols'][i], x, y);
                this.alignGrid.placeAtIndex(132 + startIndex, this.mainGameItems['lucky_symbols'][i].flip_up, x, yAnims);
                this.alignGrid.placeAtIndex(132 + startIndex, this.mainGameItems['lucky_symbols'][i].flip_down, x, yAnims);
                this.alignGrid.placeAtIndex(132 + startIndex, this.mainGameItems['lucky_symbols'][i].symbol, x, y);
                this.alignGrid.placeAtIndex(132 + startIndex, this.mainGameItems['lucky_symbols'][i].symbol.flip_up, x, yAnims);
                this.alignGrid.placeAtIndex(132 + startIndex, this.mainGameItems['lucky_symbols'][i].symbol.flip_down, x, yAnims);
                this.alignGrid.placeAtIndex(132 + startIndex, this.mainGameItems['lucky_symbols'][i].symbol.winAnimation, x, yAnims);
                startIndex += 2;
                x -= 20;
            }

            let column = 216;
            startIndex = 0;
            x = 58;
            y = -5;

            for (let i = 1; i<= 15; i++) {
                if (i === 6) {
                    x = 58;
                    column = 279;
                    startIndex = 0;
                    y = 5;
                }

                if (i === 11) {
                    x = 58;
                    column = 342;
                    startIndex = 0;
                    y = 15;
                }

                this.alignGrid.placeAtIndex(column + startIndex, this.mainGameItems['your_symbols'][i].cardGroup, x, y);
                this.alignGrid.placeAtIndex(column + startIndex, this.mainGameItems['your_symbols'][i].twinsGroup, x, y);
                startIndex += 2;
                x -= 19;
            }

            startIndex = 0;
            for (let i = 1; i <= 3; i++) {
                this.alignGrid.placeAtIndex(404 + startIndex, this.bonusGameItems[i].group, 0, 100);
                startIndex += 5;
            }

            this.alignGrid.placeAtIndex(200, this.bonusGameItems[4], -125, -400);

            if (this.bonusModeStarted) {
                startIndex = 0;

                for (let i = 1; i <= 3; i++) {
                    if (this.bonusGameItems[i].tween && this.bonusGameItems[i].tween.isPlaying()) {
                        this.bonusGameItems[i].tween.stop();
                        this.bonusGameItems[i].y = (this.alignGrid.getIndexPos(195 + startIndex).y * -1) - 50;

                        this.bonusGameItems[i].group.iterate((children) => {
                            children.y = this.bonusGameItems[i].y;
                        });
                    }

                    if (this.bonusGameItems[i].closeTween && this.bonusGameItems[i].closeTween.isPlaying()) {
                        this.stopSmoke = true;
                        this.bonusGameItems[i].closeTween.stop();

                        if (i === 2) {
                            this.bonusGameItems[i].flip_down.setScale(1);
                        }

                        this.bonusGameItems[i].flip_down.x =  (this.alignGrid.getIndexPos(190).x / 2) + ((this.bonusGameXSecond - 35) * i === 1 ? -1 : 1);

                        this.bonusGameItems[i].group.iterate(child => {
                            child.x = this.bonusGameItems[i].flip_down.x
                        });

                        this.bonusGameItems[i].flip_down.setAlpha(0);
                        this.bonusGameItems[i].setAlpha(1);
                    } else if (this.bonusSecondStage) {
                        this.bonusGameItems[i].flip_down.x =  (this.alignGrid.getIndexPos(190).x / 2) + ((this.bonusGameXSecond) * i === 1 ? -1 : 1);
                        this.bonusGameItems[i].x =  (this.alignGrid.getIndexPos(190).x / 2) + ((this.bonusGameXSecond) * i === 1 ? -1 : 1);
                        this.bonusGameItems[i].group.iterate(child => {
                            child.x = this.bonusGameItems[i].x
                        });

                        this.bonusGameItems[i].flip_down.setAlpha(0);
                        this.bonusGameItems[i].setAlpha(1);
                    }

                    this.bonusGameItems[i].y = (this.alignGrid.getIndexPos(195 + startIndex).y * -1) - 50;
                    this.bonusGameItems[i].group.iterate((children) => {
                        children.y = this.bonusGameItems[i].y;
                    });

                    if (this.bonusGameItems[i].tweenDown && this.bonusGameItems[i].tweenDown.isPlaying()) {
                        this.bonusGameItems[i].tweenDown.stop();
                        this.bonusGameItems[i].flip_up.y +=  (this.alignGrid.getIndexPos(200).y) + 150;
                        this.bonusGameItems[i].group.iterate((children) => {
                            children.y = this.bonusGameItems[i].flip_up.y;
                        });
                    } else if (this.bonusEnd) {
                        this.bonusGameItems[i].flip_up.y += (this.alignGrid.getIndexPos(200).y) + 150;
                        this.bonusGameItems[i].group.iterate((children) => {
                            children.y = this.bonusGameItems[i].flip_up.y;
                        });
                    }

                    this.bonusGameItems[i].y = (this.alignGrid.getIndexPos(195 + startIndex).y * -1) - 50;
                    this.bonusGameItems[i].group.iterate((children) => {
                        children.y = this.bonusGameItems[i].y;
                    });

                    startIndex += 5

                    if (this.bonusGameItems[4].isPlaying) {
                        this.bonusGameItems[4].stop();
                    }
                }
            }
        }

        this.alignGrid.placeAtIndex(220, this.bigWinRays, 0, 0);

        this.children.bringToTop(this.frame);
        this.children.bringToTop(this.pigOne);
        this.children.bringToTop(this.pigTwo);
        this.children.bringToTop(this.railing);
        this.children.bringToTop(this.lucky_symbols);
        this.children.bringToTop(this.shadow);
        this.children.bringToTop(this.bigWinRays);
        this.mainGameGroup.bringToTop(this.bonusGroup);
        // this.alignGrid.showNumbers();
        this.bigWinPositioner();
        this.regularWinPositioner();
    }

    createGradientSilver(item) {
        const gradient = item.context.createLinearGradient(0, -3, 0, item.height);
        gradient.addColorStop(.2, '#ffffff');
        gradient.addColorStop(.30, '#DDE4EEFF');
        gradient.addColorStop(.40, '#cdd7da');
        gradient.addColorStop(1, '#c2cbce');

        return gradient;
    }

    createGradientGold(item) {
        const gradient = item.context.createLinearGradient(0, -3, 0, item.height);
        gradient.addColorStop(.2, '#fffec0');
        gradient.addColorStop(.30, '#fff77b');
        gradient.addColorStop(.40, '#faeb4d');
        gradient.addColorStop(1, '#f5dc44');

        return gradient;
    }

    properties() {
        this.fonts = {
            normalSilver: {
                fontFamily: 'arco',
                fontSize: '65px',
                fontStyle: 'normal',
                color: '#c7d1d8',
                stroke: '#000000',
                strokeThickness: 5,
                align: 'center',
                shadow: { stroke: true, blur: 5, fill: true, color: '#000000', offsetY: 5, offsetX: 1},
            },
            winYellow: {
                fontFamily: 'arco',
                fontSize: '65px',
                fontStyle: 'normal',
                color: '#fbed49',
                stroke: '#000000',
                strokeThickness: 5,
                align: 'center',
                shadow: { stroke: true, blur: 5, fill: true, color: '#000000', offsetY: 5, offsetX: 1},
            },
            normalSilverBonus: {
                fontFamily: 'arco',
                fontSize: '130px',
                fontStyle: 'normal',
                color: '#c7d1d8',
                stroke: '#000000',
                strokeThickness: 5,
                align: 'center',
                shadow: { stroke: true, blur: 5, fill: true, color: '#000000', offsetY: 5, offsetX: 1},
            },
            winYellowBonus: {
                fontFamily: 'arco',
                fontSize: '130px',
                fontStyle: 'normal',
                color: '#fbed49',
                stroke: '#000000',
                strokeThickness: 5,
                align: 'center',
                shadow: { stroke: true, blur: 5, fill: true, color: '#000000', offsetY: 5, offsetX: 1},
            },
        }
    }
}


startGame(GameScene);